// editor-pixel.tsx - Pixel Studio: sprite editor, tilemap painter, palette creation
// A working in-browser pixel editor: drag to paint, fill, pick, palettes.
// Persists through the mode-document store as kind 'pixel' (see PixelDoc).
// Exports: PixelRoute
import { Fragment, useEffect, useRef, useState } from 'react';
import type { Dispatch, ReactElement, SetStateAction } from 'react';

import type { PoseId, Sprite2D, SpriteAnimationClip, Tilemap2D } from '../../engine';
import { saveMapDocument } from '../persistence/projectRepository';
import { useProject } from '../project/ProjectProvider';
import { createDebouncedSaver, type DebouncedSaver } from './debouncedSave';
import { EdConfirmDialog, GsToggle } from './editor-panels';
import { M2Panel, M2Label, M2ListRow } from './panelPrimitives';
import { TabStrip, type TabDescriptor } from '../../engine/editor/components/Tabs';
import { useEditorHost } from './EditorHost';
import { exportSpriteToCode, parseSpriteCode, SpriteCodeError } from './spriteCode';
import { useModeDocument } from './useModeDocument';
import { useToast } from '../design/toast';
import { inEditableTarget } from './useEditorShortcuts';

/** Suggested clip names, matching the physics engine's own `PoseId`s. A
 * sprite may still use any other name for an extra, non-physics clip. */
const POSE_ID_SUGGESTIONS: PoseId[] = [
  'idle', 'run', 'skid', 'jump', 'fall', 'wall-slide', 'dash', 'rewind', 'shadow-form', 'death-pancake',
];

/**
 * Resize a square pixel grid keeping the top-left region: growing pads with
 * empty cells, shrinking crops.
 */
export function resizePixelGrid(grid: (string | null)[], from: number, to: number): (string | null)[] {
  const next: (string | null)[] = Array(to * to).fill(null);
  const span = Math.min(from, to);
  for (let y = 0; y < span; y++) {
    for (let x = 0; x < span; x++) {
      next[y * to + x] = grid[y * from + x] ?? null;
    }
  }
  return next;
}

/** True when shrinking from -> to would crop away painted cells. */
export function resizeLosesArt(grid: (string | null)[], from: number, to: number): boolean {
  if (to >= from) return false;
  return grid.some((cell, i) => cell !== null && (i % from >= to || ((i / from) | 0) >= to));
}

/**
 * Resize a row-major fromW*fromH tile grid to toW*toH, keeping the top-left
 * region (same policy as the square sprite resize above): growing pads with
 * empty cells, shrinking crops.
 */
export function resizeTilemapGrid(
  grid: (string | null)[], fromW: number, fromH: number, toW: number, toH: number,
): (string | null)[] {
  const next: (string | null)[] = Array(toW * toH).fill(null);
  const spanW = Math.min(fromW, toW);
  const spanH = Math.min(fromH, toH);
  for (let y = 0; y < spanH; y++) {
    for (let x = 0; x < spanW; x++) {
      next[y * toW + x] = grid[y * fromW + x] ?? null;
    }
  }
  return next;
}

/** True when shrinking a fromW*fromH tilemap to toW*toH would crop away
 * painted cells. */
export function tilemapResizeLosesArt(
  grid: (string | null)[], fromW: number, fromH: number, toW: number, toH: number,
): boolean {
  if (toW >= fromW && toH >= fromH) return false;
  return grid.some((cell, i) => {
    if (cell === null) return false;
    const x = i % fromW;
    const y = (i / fromW) | 0;
    return x >= toW || y >= toH;
  });
}

/* ── grid-slice PNG/spritesheet import (R16) ──────────────────────────────
 * Deliberately not Aseprite-format import: no layers, tags, or per-frame
 * durations - a creator supplies an image and a cell size, and it's sliced
 * into the flat Sprite2D cell-grid shape Pixel Studio already edits. See
 * requirements.md R16 / design.md section 10. */

/** Smallest and largest cell size accepted, matching the existing sprite
 * resize input's own bounds. */
export const SPRITESHEET_CELL_MIN = 4;
export const SPRITESHEET_CELL_MAX = 128;
/** Upper bound on cells produced by one import, so a tiny cell size against
 * a large image fails fast with a clear message instead of silently
 * generating an unworkable number of sprites. */
export const SPRITESHEET_MAX_CELLS = 512;

/** One RGBA pixel as a Sprite2D cell colour; alpha below the threshold is
 * treated as fully transparent (a Sprite2D cell has no alpha channel of its
 * own, just colour-or-transparent). */
function pixelToCell(r: number, g: number, b: number, a: number): string | null {
  if (a < 16) return null;
  const hex = (n: number) => n.toString(16).padStart(2, '0');
  return '#' + hex(r) + hex(g) + hex(b);
}

export interface SlicedSpriteCell {
  row: number;
  col: number;
  cells: (string | null)[];
}

/**
 * Slice a spritesheet's raw RGBA pixels into a grid of `cellSize`x`cellSize`
 * Sprite2D cell grids, row-major (left to right, top to bottom, matching a
 * conventional spritesheet layout). A partial row/column that doesn't fill a
 * whole cell is dropped, and a cell that comes out fully transparent is
 * skipped - not a meaningful sprite to add.
 */
export function sliceSpritesheetPixels(
  pixels: Uint8ClampedArray, imageWidth: number, imageHeight: number, cellSize: number,
): SlicedSpriteCell[] {
  const cols = Math.floor(imageWidth / cellSize);
  const rows = Math.floor(imageHeight / cellSize);
  const out: SlicedSpriteCell[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const cells: (string | null)[] = [];
      let empty = true;
      for (let y = 0; y < cellSize; y++) {
        for (let x = 0; x < cellSize; x++) {
          const px = ((row * cellSize + y) * imageWidth + (col * cellSize + x)) * 4;
          const cell = pixelToCell(pixels[px]!, pixels[px + 1]!, pixels[px + 2]!, pixels[px + 3]!);
          if (cell !== null) empty = false;
          cells.push(cell);
        }
      }
      if (!empty) out.push({ row, col, cells });
    }
  }
  return out;
}

/** Unique, deterministic sprite names for a batch of sliced cells, avoiding
 * collisions with existing sprite ids and with each other. */
export function nameSlicedSprites(baseName: string, slices: SlicedSpriteCell[], existingNames: string[]): string[] {
  const taken = new Set(existingNames);
  return slices.map(({ row, col }) => {
    const stem = `${baseName}_${row}_${col}`;
    let name = stem;
    let n = 2;
    while (taken.has(name)) {
      name = `${stem}_${n}`;
      n += 1;
    }
    taken.add(name);
    return name;
  });
}

/** How a spritesheet import's sliced cells become sprites - see
 * `groupSlicedSprites`. */
export type SpritesheetGroupBy = 'cell' | 'row' | 'sheet';

/**
 * Assemble sliced cells into one or more sprites, per `groupBy`:
 * - `'cell'` (the original behaviour): one single-frame sprite per cell,
 *   named by its row/col - no animation, just a batch of static sprites.
 * - `'row'`: one sprite per row, that row's cells (left to right) becoming
 *   its flipbook frames - the common "each row is one animation" spritesheet
 *   convention (e.g. a walk cycle laid out across one row).
 * - `'sheet'`: the whole sheet becomes a single sprite, every slice (already
 *   row-major) becoming one flipbook frame in order.
 * `fps` only matters for `'row'`/`'sheet'` (an actual flipbook); `'cell'`
 * sprites keep the same fixed 6fps single-frame shape they always produced.
 * A row/column entirely dropped by `sliceSpritesheetPixels` (fully
 * transparent cells) simply contributes no frame at that position, rather
 * than a blank one.
 */
export function groupSlicedSprites(
  baseName: string,
  slices: SlicedSpriteCell[],
  cellSize: number,
  groupBy: SpritesheetGroupBy,
  fps: number,
  existingNames: string[],
): { name: string; sprite: PixelSpriteDoc }[] {
  if (groupBy === 'cell') {
    const names = nameSlicedSprites(baseName, slices, existingNames);
    return slices.map((slice, i) => ({
      name: names[i]!,
      sprite: { size: cellSize, clips: { default: { frames: [slice.cells], fps: 6, loop: true } }, activeClip: 'default' },
    }));
  }

  const taken = new Set(existingNames);
  function uniqueName(stem: string): string {
    let name = stem;
    let n = 2;
    while (taken.has(name)) {
      name = `${stem}_${n}`;
      n += 1;
    }
    taken.add(name);
    return name;
  }

  if (groupBy === 'sheet') {
    return [{
      name: uniqueName(baseName),
      sprite: { size: cellSize, clips: { default: { frames: slices.map((s) => s.cells), fps, loop: true } }, activeClip: 'default' },
    }];
  }

  // groupBy === 'row'
  const rows = new Map<number, SlicedSpriteCell[]>();
  for (const slice of slices) {
    const bucket = rows.get(slice.row);
    if (bucket) bucket.push(slice);
    else rows.set(slice.row, [slice]);
  }
  return [...rows.keys()].sort((a, b) => a - b).map((row) => {
    const cellsInRow = [...rows.get(row)!].sort((a, b) => a.col - b.col);
    return {
      name: uniqueName(`${baseName}_row${row}`),
      sprite: { size: cellSize, clips: { default: { frames: cellsInRow.map((s) => s.cells), fps, loop: true } }, activeClip: 'default' },
    };
  });
}

/** A safe, non-empty sprite-name stem derived from an uploaded file's name. */
export function spritesheetBaseName(fileName: string): string {
  const stem = fileName.replace(/\.[^./\\]+$/, '');
  const safe = stem.toLowerCase().replace(/[^a-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
  return safe || 'sheet';
}

export interface PixelPalette {
  name: string;
  colours: string[];
}

import type { PatrolLoopMode, TileCollisionKind, Tilemap2DEntityKind, Tilemap2DTriggerVolume, WaypointPatrol } from '../../engine/content/schema/map';

export interface PixelTile {
  id: string;
  a: string;
  b: string;
  /** Blocks the player in the 2D runtime. */
  solid?: boolean;
  /** Collision classification for platformers: 'solid', 'one-way', 'hazard', 'checkpoint'. */
  kind?: TileCollisionKind;
}

export const PIXEL_PALETTES: PixelPalette[] = [
  { name: 'Midnight', colours: ['#0e1457', '#2142d4', '#4468f6', '#71b4fe', '#bbe5fe', '#988af6', '#eef1fb', '#3c3c6f'] },
  { name: 'Ember', colours: ['#2b1a12', '#7a2e1c', '#c75c2e', '#e8975c', '#f4d27c', '#fdf3d8'] },
  { name: 'Verdant', colours: ['#11271c', '#1f5436', '#3c8c54', '#7fce8e', '#c5ecc0', '#f2f7ee'] },
];

/** Every collision classification the runtime schema supports, in the order
 * offered by the tile editor's Collision select. The 2D platformer runtime
 * (Platformer2DScene) reads these directly; the top-down runtime only ever
 * reads the simpler `solid` flag, so `patchTileCollision` below keeps both
 * in sync from a single choice. */
const TILE_COLLISION_KINDS: { value: TileCollisionKind; label: string }[] = [
  { value: 'none', label: 'None (walkable)' },
  { value: 'solid', label: 'Solid (blocks the player)' },
  { value: 'one-way', label: 'One-way platform' },
  { value: 'hazard', label: 'Hazard (damages on contact)' },
  { value: 'checkpoint', label: 'Checkpoint' },
  { value: 'crumble', label: 'Crumble (breaks after standing)' },
  { value: 'phase-barrier', label: 'Phase barrier' },
  { value: 'conveyor-left', label: 'Conveyor (left)' },
  { value: 'conveyor-right', label: 'Conveyor (right)' },
];

/** Kinds only `Platformer2DScene` interprets - selectable but inert in
 * top-down mode, since `topDown.ts` only ever reads the `solid` flag. The
 * Collision select below dims these and the runtime schema comment stays
 * accurate for both modes. */
const PLATFORMER_ONLY_COLLISION_KINDS = new Set<TileCollisionKind>([
  'hazard', 'checkpoint', 'crumble', 'phase-barrier', 'conveyor-left', 'conveyor-right',
]);

/** The map's movement model, authored per project and written onto
 * `Tilemap2D.mode`; see the toggle in the tilemap panel below. */
export type PixelMapMode = 'top-down' | 'platformer';

/** Every authoring category a placed entity can carry, in the order offered
 * by the Place mode Kind select. A label only (see `Tilemap2DEntityKind`) -
 * real triggers are placed with the Trigger placement tool instead (see
 * `PixelTriggerDoc`), not authored as an entity kind. */
const ENTITY_KINDS: { value: Tilemap2DEntityKind; label: string }[] = [
  { value: 'decoration', label: 'Decoration' },
  { value: 'npc', label: 'NPC' },
  { value: 'enemy', label: 'Enemy' },
  { value: 'item', label: 'Item' },
];

export const PIXEL_TILES: PixelTile[] = [
  { id: 'grass', a: '#3c8c54', b: '#2f7044' },
  { id: 'water', a: '#2c6fd4', b: '#2459ab', solid: true },
  { id: 'stone', a: '#6e7480', b: '#575c66', solid: true },
  { id: 'sand', a: '#d8c08a', b: '#c4ab74' },
  { id: 'lava', a: '#c75c2e', b: '#a34621', solid: true },
  { id: 'void', a: '#101013', b: '#0a0a0c', solid: true },
];

export type PixelMapLayer = 'background' | 'main' | 'foreground';

/** One tilemap cell, composited across all three layers so painting one
 * still shows the other two as reference: the layer being painted at full
 * opacity, the other two dimmed underneath, in the same back-to-front order
 * the runtime itself composites them in (see Tilemap2DScene's drawTilemap /
 * drawForegroundLayer). Defined ahead of TileCell's own declaration in the
 * file, which is fine since function declarations are hoisted. */
export function LayeredTileCell({
  background, main, foreground, active, size, tiles = PIXEL_TILES,
}: {
  background: string | null;
  main: string | null;
  foreground: string | null;
  active: PixelMapLayer;
  size: number;
  tiles?: PixelTile[];
}) {
  const layers: { cell: string | null; dim: boolean }[] = [
    { cell: background, dim: active !== 'background' },
    { cell: main, dim: active !== 'main' },
    { cell: foreground, dim: active !== 'foreground' },
  ];
  return (
    <div style={{ position: 'relative', width: size, height: size, background: '#0c0c0e' }}>
      {layers.map((l, i) => l.cell && (
        <div key={i} style={{ position: 'absolute', inset: 0, opacity: l.dim ? 0.3 : 1 }}>
          <TileCell tile={l.cell} size={size} tiles={tiles} />
        </div>
      ))}
    </div>
  );
}

export type PxToolKind = 'pencil' | 'eraser' | 'fill' | 'pick';

const PX_TOOL_HOTKEYS: Record<PxToolKind, string> = { pencil: 'B', eraser: 'E', fill: 'G', pick: 'I' };

export function PxToolIcon({ kind }: { kind: PxToolKind }) {
  const p: Record<PxToolKind, ReactElement> = {
    pencil: <path d="M10.8 2.2l3 3L5.5 13.5 2 14l.5-3.5 8.3-8.3z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" fill="none" />,
    eraser: <path d="M6 13.5L2.5 10a1.4 1.4 0 010-2l5.5-5.5a1.4 1.4 0 012 0l3.5 3.5a1.4 1.4 0 010 2L9 12.5M6 13.5h7.5M6 13.5L4.5 12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />,
    fill: <path d="M7.5 1.5l5.5 5.5-5 5a1.4 1.4 0 01-2 0L2.5 8.5l5-5zM3 8h9M13.5 11.5c.6.9 1 1.6 1 2.2a1 1 0 11-2 0c0-.6.4-1.3 1-2.2z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" fill="none" />,
    pick: <path d="M9.5 2.5l4 4-1.5 1.5-4-4 1.5-1.5zM8.5 5.5l-6 6-.5 2.5 2.5-.5 6-6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />,
  };
  return <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">{p[kind]}</svg>;
}

/** A curved undo arrow; `flip` mirrors it horizontally for redo. */
export function PxUndoIcon({ flip }: { flip: boolean }) {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true" style={flip ? { transform: 'scaleX(-1)' } : undefined}>
      <path d="M4 5H10.5A3.5 3.5 0 0114 8.5 3.5 3.5 0 0110.5 12H6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none" />
      <path d="M6.5 2.2L3.5 5l3 2.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

/** A filled play triangle, or two pause bars when `playing`. */
export function PxPlayPauseIcon({ playing }: { playing: boolean }) {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
      {playing ? (
        <g fill="currentColor"><rect x="4" y="3" width="3" height="10" rx="0.5" /><rect x="9" y="3" width="3" height="10" rx="0.5" /></g>
      ) : (
        <path d="M4.5 3v10l8-5-8-5z" fill="currentColor" />
      )}
    </svg>
  );
}

/** A tiny rendered preview of one frame's actual pixels, for the flipbook
 * strip - previously just a numbered button, indistinguishable from its
 * neighbours at a glance in a longer clip. */
function FrameThumb({ cells, size }: { cells: (string | null)[]; size: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const c = cells[y * size + x];
        ctx.fillStyle = c ?? ((x + y) % 2 === 0 ? '#18181c' : '#101013');
        ctx.fillRect(x, y, 1, 1);
      }
    }
  }, [cells, size]);
  return <canvas ref={canvasRef} width={size} height={size} style={{ width: 20, height: 20, borderRadius: 4, imageRendering: 'pixelated' }} />;
}

/* ── sprite canvas ────────────────────────────────────────────────────── */
export interface SpriteCanvasProps {
  size: number;
  grid: (string | null)[];
  onPaint: (i: number) => void;
  painting: { current: boolean };
  /** Onion skin: the previous frame's cells, ghosted under empty cells. */
  under?: (string | null)[] | null;
  /** Multiplier on the base cell size (which already shrinks to fit larger
   * canvases); lets a large custom size stay workable. Defaults to 1. */
  zoom?: number;
}

/** A hex colour at reduced opacity, for the onion-skin ghost. */
function ghost(hex: string): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return 'rgba(255,255,255,0.18)';
  const n = parseInt(m[1]!, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, 0.28)`;
}

export function SpriteCanvas({ size, grid, onPaint, painting, under, zoom = 1 }: SpriteCanvasProps) {
  const cell = Math.max(2, Math.floor(Math.min(520 / size, 24) * zoom));
  return (
    <div
      onMouseDown={(e) => { e.preventDefault(); painting.current = true; }}
      style={{
        display: 'grid', gridTemplateColumns: 'repeat(' + size + ', ' + cell + 'px)',
        border: '1px solid var(--ss-border)', borderRadius: 4, overflow: 'hidden',
        boxShadow: '0 30px 80px rgba(0,0,0,0.5)', cursor: 'crosshair', userSelect: 'none',
      }}>
      {grid.map((c, i) => {
        const cx = i % size, cy = (i / size) | 0;
        const checker = (cx + cy) % 2 === 0 ? '#131316' : '#0e0e11';
        const onion = !c && under?.[i] ? ghost(under[i]!) : null;
        return (
          <div key={i}
            onMouseDown={() => onPaint(i)}
            onMouseEnter={() => { if (painting.current) onPaint(i); }}
            style={{ width: cell, height: cell, background: c ?? checker, ...(onion ? { backgroundImage: `linear-gradient(${onion}, ${onion})` } : {}) }} />
        );
      })}
    </div>
  );
}

/* ── tilemap canvas ───────────────────────────────────────────────────── */
export function TileCell({ tile, size, tiles = PIXEL_TILES }: { tile: string | null; size: number; tiles?: PixelTile[] }) {
  if (!tile) return <div style={{ width: size, height: size, background: '#0c0c0e' }} />;
  const t = tiles.find((x) => x.id === tile);
  if (!t) return <div style={{ width: size, height: size, background: '#0c0c0e' }} />;
  return (
    <div style={{
      width: size, height: size,
      backgroundImage: 'linear-gradient(45deg, ' + t.b + ' 25%, transparent 25%, transparent 75%, ' + t.b + ' 75%)',
      backgroundColor: t.a, backgroundSize: (size / 2) + 'px ' + (size / 2) + 'px',
    }} />
  );
}

/* ── palette panel ────────────────────────────────────────────────────── */
export interface PalettePanelProps {
  palettes: PixelPalette[];
  setPalettes: Dispatch<SetStateAction<PixelPalette[]>>;
  activePal: number;
  setActivePal: Dispatch<SetStateAction<number>>;
  colour: string;
  setColour: Dispatch<SetStateAction<string>>;
  onDeletePalette: (index: number) => void;
}

export function PalettePanel({ palettes, setPalettes, activePal, setActivePal, colour, setColour, onDeletePalette }: PalettePanelProps) {
  const pal = palettes[activePal];
  if (!pal) return null;
  // The built-in palettes (Midnight/Ember/Verdant) always occupy the first
  // PIXEL_PALETTES.length slots; only a project-added palette past that is
  // editable, matching the existing rename gating below.
  const custom = activePal >= PIXEL_PALETTES.length;
  return (
    <Fragment>
      <M2Label action={
        <button className="ss-btn ss-btn--ghost" style={{ padding: '3px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}
          onClick={() => { setPalettes([...palettes, { name: 'Untitled palette', colours: [colour] }]); setActivePal(palettes.length); }}>+ New</button>
      }>Palette</M2Label>
      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <select className="ss-input" value={activePal} onChange={(e) => setActivePal(+e.target.value)}
          style={{ appearance: 'none', padding: '8px 11px', fontSize: 'var(--ss-fs-sm)', borderRadius: 9 }}>
          {palettes.map((p, i) => <option key={i} value={i}>{p.name}</option>)}
        </select>
        {custom && (
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="ss-input" value={pal.name}
              onChange={(e) => setPalettes(palettes.map((p, i) => i === activePal ? { ...p, name: e.target.value } : p))}
              style={{ flex: 1, minWidth: 0, padding: '8px 11px', fontSize: 'var(--ss-fs-sm)', borderRadius: 9 }} />
            <button className="ss-btn ss-btn--ghost" onClick={() => onDeletePalette(activePal)} title="Delete palette"
              style={{ padding: '4px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, color: 'var(--ss-danger)', flexShrink: 0 }}>Delete</button>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6 }}>
          {pal.colours.map((c, i) => (
            <div key={c + i} style={{ position: 'relative' }}>
              <button onClick={() => setColour(c)} title={c} style={{
                width: '100%', aspectRatio: '1', borderRadius: 7, background: c, cursor: 'pointer',
                border: '2px solid ' + (colour === c ? 'var(--ss-accent)' : 'rgba(255,255,255,0.1)'),
              }} />
              {custom && pal.colours.length > 1 && (
                <button
                  onClick={() => setPalettes(palettes.map((p, pi) => pi === activePal ? { ...p, colours: p.colours.filter((_, ci) => ci !== i) } : p))}
                  title="Remove from palette" aria-label={'Remove ' + c + ' from palette'}
                  style={{
                    position: 'absolute', top: -5, right: -5, width: 14, height: 14, borderRadius: '50%',
                    border: '1px solid var(--ss-border)', background: 'var(--ss-bg-2)', color: 'var(--ss-text-3)',
                    fontSize: 'var(--ss-fs-3xs)', lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>×</button>
              )}
            </div>
          ))}
          <button onClick={() => {
            if (!pal.colours.includes(colour)) {
              setPalettes(palettes.map((p, i) => i === activePal ? { ...p, colours: [...p.colours, colour] } : p));
            }
          }} title="Add active colour to palette" style={{
            aspectRatio: '1', borderRadius: 7, background: 'transparent', cursor: 'pointer',
            border: '1px dashed var(--ss-border)', color: 'var(--ss-text-3)', fontSize: 'var(--ss-fs-md)',
          }}>+</button>
        </div>
      </div>
      <M2Label>Active colour</M2Label>
      <div style={{ padding: '0 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <label style={{ position: 'relative', width: 38, height: 38, borderRadius: 9, overflow: 'hidden', border: '1px solid var(--ss-border)', cursor: 'pointer', background: colour, flexShrink: 0 }}>
          <input type="color" value={colour} onChange={(e) => setColour(e.target.value)}
            style={{ position: 'absolute', inset: -8, opacity: 0, cursor: 'pointer' }} />
        </label>
        <input className="ss-input" value={colour} onChange={(e) => setColour(e.target.value)}
          style={{ fontFamily: 'var(--ss-mono)', fontSize: 'var(--ss-fs-xs)', padding: '8px 11px', borderRadius: 9 }} />
      </div>
    </Fragment>
  );
}

/* ── Pixel Studio route ───────────────────────────────────────────────── */
export type PixelView = 'sprite' | 'tilemap' | 'entities';
const PIXEL_VIEW_TABS: TabDescriptor[] = [
  { id: 'sprite', label: 'Sprite' },
  { id: 'tilemap', label: 'Tilemap' },
  { id: 'entities', label: 'Place' },
];

/** Sprite canvas size presets, offered as a segmented control alongside the
 * Custom input below it. */
const PIXEL_CANVAS_SIZE_TABS: TabDescriptor[] = [16, 24, 32, 48, 64].map((n) => ({ id: String(n), label: String(n) }));

const PIXEL_MODE_TABS: TabDescriptor[] = [
  { id: 'top-down', label: 'Top-down' },
  { id: 'platformer', label: 'Platformer' },
];

const PIXEL_LAYER_TABS: TabDescriptor[] = [
  { id: 'background', label: 'Background' },
  { id: 'main', label: 'Main' },
  { id: 'foreground', label: 'Foreground' },
];

const PIXEL_PLACE_TOOL_TABS: TabDescriptor[] = [
  { id: 'sprite', label: 'Sprite' },
  { id: 'spawn', label: 'Spawn' },
  { id: 'trigger', label: 'Trigger' },
];

/** One named animation clip within a sprite: a flipbook (frames[0] always
 * exists) at its own speed and loop behaviour. */
export interface PixelClip {
  frames: (string | null)[][];
  fps: number;
  loop: boolean;
}

/** One sprite document: one or more named clips at one size. A single clip
 * named 'default' is the common case for simple, non-player art; a player
 * sprite typically has one clip per `PoseId` the physics engine can select. */
export interface PixelSpriteDoc {
  size: number;
  clips: Record<string, PixelClip>;
  /** Which clip the frame strip and canvas are currently editing. */
  activeClip: string;
  /** Legacy single-flipbook fields (pre-clips docs); consumed by migration. */
  frames?: (string | null)[][];
  fps?: number;
}

/** A sprite placed in the tilemap world - the 2D analogue of a 3D
 * `MapObjectInstance`. `spriteId` names an entry in the doc's `sprites`. */
export interface PixelEntityDoc {
  id: string;
  spriteId: string;
  x: number;
  y: number;
  /** Blocks the player like a solid tile; set from the Place mode panel at
   * placement time (see `Tilemap2DEntity.blocking`). */
  blocking: boolean;
  /** Authoring label only for most kinds; `'npc'` is what the Patrol panel
   * filters on (see `Tilemap2DEntityKind`). */
  kind: Tilemap2DEntityKind;
  /** Optional authored waypoint route (see `WaypointPatrol`); authored via
   * the Patrol panel in Place mode. Omitted leaves the entity static. */
  patrol?: WaypointPatrol;
}

/** A placed trigger volume - the tile-grid equivalent of a 3D
 * `MapTriggerVolume`. `x`/`y` is the footprint's top-left cell (matching
 * `PixelEntityDoc`'s own anchor convention); `w`/`h` its size in tiles.
 * Compiled to a `Tilemap2DTriggerVolume` on save (center = anchor + size/2). */
export interface PixelTriggerDoc {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  kind: Tilemap2DTriggerVolume['kind'];
  eventName?: string;
  /** Set only for `kind: 'teleport'`; the tile the player lands on. */
  targetX?: number;
  targetY?: number;
}

const DEFAULT_MAP_WIDTH = 24;
const DEFAULT_MAP_HEIGHT = 14;

/** The 'pixel' mode document: everything the creator authors in this mode.
 * Multi-sprite with flipbooks; older single-grid docs migrate on load. */
export type PixelDoc = {
  view: PixelView;
  map: (string | null)[];
  /** Painted behind `map`; see Tilemap2D.backgroundCells. */
  backgroundMap: (string | null)[];
  /** Painted in front of the player; see Tilemap2D.foregroundCells. */
  foregroundMap: (string | null)[];
  mapWidth: number;
  mapHeight: number;
  /** Movement model for this project's 2D map; see `Tilemap2D.mode`. */
  mode: PixelMapMode;
  colour: string;
  palettes: PixelPalette[];
  activePal: number;
  sprites: Record<string, PixelSpriteDoc>;
  /** The sprite the 2D runtime plays as the player. */
  player: string;
  /** The tile kinds paintable in this project - starts as the built-in set,
   * extendable per project. */
  tiles: PixelTile[];
  /** Non-player sprites placed in the world. */
  entities: PixelEntityDoc[];
  /** The player's spawn cell; see `Tilemap2D.spawn`. `null` (the default)
   * leaves the runtime to fall back to the first walkable cell. */
  spawn: { x: number; y: number } | null;
  /** Placed trigger volumes; see `PixelTriggerDoc`/`Tilemap2D.triggers`. */
  triggers: PixelTriggerDoc[];
  /** Legacy single-grid fields (pre-flipbook docs); consumed by migration. */
  size?: number;
  grid?: (string | null)[];
  sprite?: string;
};

function blankSprite(size = 24): PixelSpriteDoc {
  return { size, clips: { default: { frames: [Array(size * size).fill(null)], fps: 6, loop: true } }, activeClip: 'default' };
}

/** Upgrade a stored sprite doc to the current clips shape (idempotent). Old
 * docs saved before named clips existed carried a single `frames`/`fps`
 * flipbook directly; that becomes a lone clip named 'default'. */
export function migratePixelSpriteDoc(s: PixelSpriteDoc): PixelSpriteDoc {
  if (s.clips && s.activeClip && s.clips[s.activeClip]) return s;
  const frames = s.frames && s.frames.length > 0 ? s.frames : [Array(s.size * s.size).fill(null)];
  return { size: s.size, clips: { default: { frames, fps: s.fps ?? 6, loop: true } }, activeClip: 'default' };
}

function migrateSprites(sprites: Record<string, PixelSpriteDoc>): Record<string, PixelSpriteDoc> {
  return Object.fromEntries(Object.entries(sprites).map(([name, s]) => [name, migratePixelSpriteDoc(s)]));
}

/** A short "· 3 clips" / "· 8f" suffix for the sprite list: whether a
 * sprite has multiple named clips, or just a multi-frame single flipbook. */
function spriteSummaryLabel(s: PixelSpriteDoc): string {
  const clipCount = Object.keys(s.clips).length;
  if (clipCount > 1) return ' · ' + clipCount + ' clips';
  const sole = Object.values(s.clips)[0];
  return sole && sole.frames.length > 1 ? ' · ' + sole.frames.length + 'f' : '';
}

/** Resize every clip's every frame together, keeping art in place (see
 * resizePixelGrid) - a sprite's clips always share one canvas size. */
export function resizeSpriteDoc(s: PixelSpriteDoc, to: number): PixelSpriteDoc {
  return {
    ...s,
    size: to,
    clips: Object.fromEntries(
      Object.entries(s.clips).map(([name, c]) => [name, { ...c, frames: c.frames.map((f) => resizePixelGrid(f, s.size, to)) }]),
    ),
  };
}

/** True when shrinking a sprite to `to` would crop painted cells in any of
 * its clips, not just the one currently being viewed. */
export function spriteResizeLosesArt(s: PixelSpriteDoc, to: number): boolean {
  return Object.values(s.clips).some((c) => c.frames.some((f) => resizeLosesArt(f, s.size, to)));
}

/**
 * Compile a sprite doc to the runtime's Sprite2D wire shape under the given
 * id. Every sprite emits both: the flat cells/frames/fps flipbook, mirroring
 * its active clip (Tilemap2DScene and placed entities only ever read this
 * flat shape), and `animations`, one entry per authored clip
 * (Platformer2DScene's state machine looks clips up here by PoseId name).
 * Pulled out of `spriteWire` so a tile's baked art - which has no entry of
 * its own in a project's `sprites` map - can be compiled the same way (see
 * `tileArtSpriteKey` below).
 */
export function compileSpriteDoc(id: string, s: PixelSpriteDoc): Sprite2D {
  const activeClip = s.clips[s.activeClip] ?? Object.values(s.clips)[0]!;
  const animations: Record<string, SpriteAnimationClip> = {};
  for (const [clipName, clip] of Object.entries(s.clips)) {
    animations[clipName] = { frames: clip.frames, fps: clip.fps, loop: clip.loop };
  }
  return {
    id,
    size: s.size,
    cells: activeClip.frames[0]!,
    ...(activeClip.frames.length > 1 ? { frames: activeClip.frames.slice(1), fps: activeClip.fps } : {}),
    animations,
  };
}

/**
 * Compile a sprite to the runtime's wire shape. A missing sprite name falls
 * back to a blank 24x24 sprite rather than throwing.
 */
export function spriteWire(sprites: Record<string, PixelSpriteDoc>, name: string): Sprite2D {
  return compileSpriteDoc(name, sprites[name] ?? blankSprite(24));
}

/**
 * A tile's baked art (`Tilemap2DTile.art`) is authored by opening it into
 * the same sprite canvas and clip machinery every other sprite already
 * uses, rather than a second editing surface - see `PixelRoute`'s
 * `openTileArtInStudio`. While being edited it lives under a reserved key in
 * the project's ordinary `sprites` map, so `active`, undo/redo, the
 * palette, the clip list and the code export/import buttons all work on it
 * unchanged. It is filtered back out (see `isTileArtSpriteKey`) before that
 * map is shown in the Sprites list or compiled into
 * `Tilemap2D.sprites`/`playerSprite`; `compileSpriteDoc` turns it into the
 * tile's own `art` field on save, keyed by the tile's plain id rather than
 * this internal one.
 */
const TILE_ART_SPRITE_PREFIX = '@tile:';

export function tileArtSpriteKey(tileId: string): string {
  return TILE_ART_SPRITE_PREFIX + tileId;
}

export function isTileArtSpriteKey(name: string): boolean {
  return name.startsWith(TILE_ART_SPRITE_PREFIX);
}

/** Drop a tile's baked-art sprite entry, e.g. when the owning tile is
 * deleted or its art is explicitly cleared. A no-op when the tile has none. */
export function withoutTileArtSprite(sprites: Record<string, PixelSpriteDoc>, tileId: string): Record<string, PixelSpriteDoc> {
  const key = tileArtSpriteKey(tileId);
  if (!(key in sprites)) return sprites;
  const next = { ...sprites };
  delete next[key];
  return next;
}

/** Carry a tile's baked-art sprite entry across a tile rename, so renaming a
 * tile doesn't orphan its art under the old id. A no-op when the tile has
 * none. */
export function renameTileArtSprite(
  sprites: Record<string, PixelSpriteDoc>, oldTileId: string, newTileId: string,
): Record<string, PixelSpriteDoc> {
  const oldKey = tileArtSpriteKey(oldTileId);
  const doc = sprites[oldKey];
  if (!doc) return sprites;
  const rest = { ...sprites };
  delete rest[oldKey];
  return { ...rest, [tileArtSpriteKey(newTileId)]: doc };
}

/** Fields added after the multi-sprite shape landed; backfilled onto any doc
 * missing them, whether or not it also needs the older single-grid migration. */
function withCurrentFieldDefaults(
  doc: PixelDoc,
): Pick<PixelDoc, 'mapWidth' | 'mapHeight' | 'mode' | 'tiles' | 'entities' | 'backgroundMap' | 'foregroundMap' | 'spawn' | 'triggers'> {
  const mapWidth = doc.mapWidth ?? DEFAULT_MAP_WIDTH;
  const mapHeight = doc.mapHeight ?? DEFAULT_MAP_HEIGHT;
  const blankLayer = () => Array(mapWidth * mapHeight).fill(null);
  return {
    mapWidth,
    mapHeight,
    mode: doc.mode ?? 'top-down',
    tiles: doc.tiles && doc.tiles.length > 0 ? doc.tiles : PIXEL_TILES,
    entities: (doc.entities ?? []).map((e) => ({ ...e, blocking: e.blocking ?? false, kind: e.kind ?? 'decoration' })),
    backgroundMap: doc.backgroundMap && doc.backgroundMap.length === mapWidth * mapHeight ? doc.backgroundMap : blankLayer(),
    foregroundMap: doc.foregroundMap && doc.foregroundMap.length === mapWidth * mapHeight ? doc.foregroundMap : blankLayer(),
    spawn: doc.spawn ?? null,
    triggers: doc.triggers ?? [],
  };
}

/** Upgrade any stored pixel doc to the current shape (idempotent). Every
 * sprite is passed through `migratePixelSpriteDoc` regardless of which
 * branch the document itself takes, since a doc can already be in the
 * current multi-sprite shape while individual sprites still predate clips. */
export function migratePixelDoc(doc: PixelDoc): PixelDoc {
  if (doc.sprites && doc.player && doc.sprites[doc.player]) {
    return { ...doc, ...withCurrentFieldDefaults(doc), sprites: migrateSprites(doc.sprites) };
  }
  const name = doc.sprite ?? 'player_idle';
  const size = doc.size ?? 24;
  const legacyFrames = doc.grid && doc.grid.length === size * size ? [doc.grid] : blankSprite(size).clips.default!.frames;
  const rawSprite = doc.sprites?.[name] ?? ({ size, frames: legacyFrames, fps: 6 } as PixelSpriteDoc);
  return {
    view: doc.view ?? 'sprite',
    map: doc.map ?? Array(DEFAULT_MAP_WIDTH * DEFAULT_MAP_HEIGHT).fill(null),
    colour: doc.colour ?? '#71b4fe',
    palettes: doc.palettes ?? PIXEL_PALETTES,
    activePal: doc.activePal ?? 0,
    sprites: { ...migrateSprites(doc.sprites ?? {}), [name]: migratePixelSpriteDoc(rawSprite) },
    player: name,
    ...withCurrentFieldDefaults(doc),
  };
}

const PIXEL_DOC_FALLBACK: PixelDoc = {
  view: 'sprite',
  map: Array(DEFAULT_MAP_WIDTH * DEFAULT_MAP_HEIGHT).fill(null),
  backgroundMap: Array(DEFAULT_MAP_WIDTH * DEFAULT_MAP_HEIGHT).fill(null),
  foregroundMap: Array(DEFAULT_MAP_WIDTH * DEFAULT_MAP_HEIGHT).fill(null),
  mapWidth: DEFAULT_MAP_WIDTH,
  mapHeight: DEFAULT_MAP_HEIGHT,
  mode: 'top-down',
  colour: '#71b4fe',
  palettes: PIXEL_PALETTES,
  activePal: 0,
  sprites: { player_idle: blankSprite(24) },
  player: 'player_idle',
  tiles: PIXEL_TILES,
  entities: [],
  spawn: null,
  triggers: [],
};

/** Decode an uploaded image file into its raw RGBA pixels via an offscreen
 * canvas, for `sliceSpritesheetPixels` to grid-slice. Rejects with a
 * creator-facing message on an unreadable or corrupt file. */
async function decodeImageFile(file: File): Promise<{ pixels: Uint8ClampedArray; width: number; height: number }> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('Could not read that as an image file.');
  }
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not read that as an image file.');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return { pixels: data, width: canvas.width, height: canvas.height };
}

/**
 * Grid-slice PNG/spritesheet import (R16): a creator supplies an image and a
 * cell size, and it's sliced into sprites at that size - a flat grid slice
 * only, not Aseprite-format import (no layers, tags, or per-frame
 * durations).
 *
 * "Import as" (`groupBy`) has no useful default: a sheet of unrelated icons
 * genuinely wants 'cell' (one static sprite per icon), while a walk-cycle
 * sheet genuinely wants 'row' or 'sheet' (one animated sprite) - and getting
 * either wrong isn't a difference of degree, it's a different, confusing
 * result (icons silently glued into one bogus animation, or a walk cycle
 * scattered into unconnected static sprites). Rather than guess, the choice
 * starts unset and the creator must pick one explicitly before Import
 * enables - see the placeholder option and the `!groupBy` guards below.
 */
export function PxSpritesheetImportDialog({
  existingNames, onImport, onCancel,
}: {
  existingNames: string[];
  onImport: (sprites: { name: string; sprite: PixelSpriteDoc }[]) => void;
  onCancel: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [cellSize, setCellSize] = useState(16);
  const [groupBy, setGroupBy] = useState<SpritesheetGroupBy | ''>('');
  const [fps, setFps] = useState(6);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function doImport() {
    if (!file) {
      setError('Choose an image file.');
      return;
    }
    if (!groupBy) {
      setError('Choose how to import the sheet.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { pixels, width, height } = await decodeImageFile(file);
      const slices = sliceSpritesheetPixels(pixels, width, height, cellSize);
      if (slices.length === 0) {
        setError(`No ${cellSize}px cells fit that image - check the cell size.`);
        return;
      }
      if (slices.length > SPRITESHEET_MAX_CELLS) {
        setError(`That cell size would produce ${slices.length} sprites (limit ${SPRITESHEET_MAX_CELLS}) - try a larger cell size.`);
        return;
      }
      onImport(groupSlicedSprites(spritesheetBaseName(file.name), slices, cellSize, groupBy, fps, existingNames));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that as an image file.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(4,4,6,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Import PNG or spritesheet" style={{
        width: 380, background: 'var(--ss-bg-2)', border: '1px solid var(--ss-border)', borderRadius: 14,
        padding: '20px 22px 18px', boxShadow: '0 30px 80px rgba(0,0,0,0.55)',
      }}>
        <h3 style={{ fontFamily: 'var(--ss-display)', fontSize: 'var(--ss-fs-lg)', fontWeight: 500, letterSpacing: '-0.01em', marginBottom: 8 }}>
          Import PNG / spritesheet
        </h3>
        <p style={{ fontSize: 'var(--ss-fs-sm)', color: 'var(--ss-text-2)', lineHeight: 1.55, marginBottom: 14 }}>
          Slices the image into sprites of the chosen cell size, left to right then top to bottom. A flat grid slice only - layers, tags and per-frame durations aren&rsquo;t read.
        </p>
        <label style={{ display: 'block', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', marginBottom: 6 }}>Image</label>
        <input
          type="file"
          accept="image/png,image/*"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          style={{ width: '100%', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-2)', marginBottom: 14 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', marginBottom: 16 }}>
          Cell size
          <input
            className="ss-input"
            type="number"
            min={SPRITESHEET_CELL_MIN}
            max={SPRITESHEET_CELL_MAX}
            value={cellSize}
            onChange={(e) => {
              const v = Math.round(Number(e.target.value));
              if (Number.isFinite(v)) setCellSize(Math.min(SPRITESHEET_CELL_MAX, Math.max(SPRITESHEET_CELL_MIN, v)));
            }}
            style={{ width: 64, padding: '5px 7px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, textAlign: 'center' }}
          />
          <span>px</span>
        </label>
        <label style={{ display: 'block', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', marginBottom: 6 }}>Import as (required)</label>
        <select
          className="ss-input"
          required
          aria-required="true"
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as SpritesheetGroupBy | '')}
          style={{ width: '100%', appearance: 'none', padding: '7px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 8, marginBottom: 10 }}
        >
          <option value="" disabled>Choose how to import…</option>
          <option value="cell">One sprite per cell (no animation)</option>
          <option value="row">One animated sprite per row</option>
          <option value="sheet">One animated sprite (whole sheet)</option>
        </select>
        {(groupBy === 'row' || groupBy === 'sheet') && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', marginBottom: 12 }}>
            Playback
            <input
              className="ss-input"
              type="number"
              min={1}
              max={30}
              value={fps}
              onChange={(e) => {
                const v = Math.round(Number(e.target.value));
                if (Number.isFinite(v)) setFps(Math.min(30, Math.max(1, v)));
              }}
              style={{ width: 56, padding: '5px 7px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, textAlign: 'center' }}
            />
            <span>fps</span>
          </label>
        )}
        <p style={{ fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.5, marginBottom: 14 }}>
          {groupBy === 'cell'
            ? 'Each sliced cell becomes its own static sprite - the right choice for a sheet of unrelated pieces, e.g. item icons.'
            : groupBy === 'row'
            ? 'Each row of cells becomes one sprite, its cells (left to right) playing as that sprite’s frames - the usual layout for a walk cycle or similar.'
            : groupBy === 'sheet'
            ? 'Every sliced cell becomes one frame of a single sprite, in sheet order.'
            : 'Pick one: a sheet of separate static pieces (icons, items) wants "per cell". A sheet of one thing’s animation frames wants "per row" or "whole sheet".'}
        </p>
        {error && <p style={{ fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-danger)', lineHeight: 1.5, marginBottom: 10 }} role="alert">{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="ss-btn ss-btn--ghost" style={{ padding: '7px 14px', fontSize: 'var(--ss-fs-sm)', borderRadius: 9 }} onClick={onCancel}>Cancel</button>
          <button
            className="ss-btn ss-btn--primary"
            disabled={!file || !groupBy || busy}
            style={{ padding: '7px 14px', fontSize: 'var(--ss-fs-sm)', borderRadius: 9 }}
            onClick={() => void doImport()}
          >
            {busy ? 'Importing…' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PixelRoute() {
  const [view, setView] = useState<PixelView>('sprite');
  const [map, setMap] = useState<(string | null)[]>(() => Array(DEFAULT_MAP_WIDTH * DEFAULT_MAP_HEIGHT).fill(null));
  const [backgroundMap, setBackgroundMap] = useState<(string | null)[]>(() => Array(DEFAULT_MAP_WIDTH * DEFAULT_MAP_HEIGHT).fill(null));
  const [foregroundMap, setForegroundMap] = useState<(string | null)[]>(() => Array(DEFAULT_MAP_WIDTH * DEFAULT_MAP_HEIGHT).fill(null));
  const [mapLayer, setMapLayer] = useState<PixelMapLayer>('main');
  const [mapWidth, setMapWidth] = useState(DEFAULT_MAP_WIDTH);
  const [mapHeight, setMapHeight] = useState(DEFAULT_MAP_HEIGHT);
  const [mode, setMode] = useState<PixelMapMode>('top-down');
  const [tiles, setTiles] = useState<PixelTile[]>(PIXEL_TILES);
  const [entities, setEntities] = useState<PixelEntityDoc[]>([]);
  const [placeBlocking, setPlaceBlocking] = useState(false);
  const [placeKind, setPlaceKind] = useState<Tilemap2DEntityKind>('decoration');
  const [spawn, setSpawn] = useState<{ x: number; y: number } | null>(null);
  const [triggers, setTriggers] = useState<PixelTriggerDoc[]>([]);
  const [placeTool, setPlaceTool] = useState<'sprite' | 'spawn' | 'trigger'>('sprite');
  const [triggerKind, setTriggerKind] = useState<Tilemap2DTriggerVolume['kind']>('event');
  const [triggerEventName, setTriggerEventName] = useState('');
  const [triggerW, setTriggerW] = useState(1);
  const [triggerH, setTriggerH] = useState(1);
  const [triggerTargetX, setTriggerTargetX] = useState(0);
  const [triggerTargetY, setTriggerTargetY] = useState(0);
  const [tool, setTool] = useState<PxToolKind>('pencil');
  const [tile, setTile] = useState('grass');
  const [colour, setColour] = useState('#71b4fe');
  const [palettes, setPalettes] = useState<PixelPalette[]>(PIXEL_PALETTES);
  const [activePal, setActivePal] = useState(0);
  const [sprites, setSprites] = useState<Record<string, PixelSpriteDoc>>({ player_idle: blankSprite(24) });
  const [player, setPlayer] = useState('player_idle');
  const [active, setActive] = useState('player_idle');
  const [frame, setFrame] = useState(0);
  const [onion, setOnion] = useState(true);
  const [spriteZoom, setSpriteZoom] = useState(1);
  const ZOOM_STEPS = [0.5, 1, 1.5, 2, 3, 4];
  const painting = useRef(false);
  useEffect(() => {
    const up = () => { painting.current = false; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  // Undo/redo for painting only (sprite pixels and tilemap cells): a
  // snapshot of both is pushed once per stroke, before Fill or Eraser (or a
  // pencil drag) touches anything, so a mis-click on a large region has a
  // way back beyond manual repainting.
  const UNDO_LIMIT = 50;
  interface PxSnapshot {
    map: (string | null)[];
    backgroundMap: (string | null)[];
    foregroundMap: (string | null)[];
    sprites: Record<string, PixelSpriteDoc>;
    entities: PixelEntityDoc[];
  }
  const [undoStack, setUndoStack] = useState<PxSnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<PxSnapshot[]>([]);
  function pushUndoSnapshot() {
    setUndoStack((stack) => [...stack, { map, backgroundMap, foregroundMap, sprites, entities }].slice(-UNDO_LIMIT));
    setRedoStack([]);
  }
  function undo() {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1]!;
    setRedoStack((stack) => [...stack, { map, backgroundMap, foregroundMap, sprites, entities }]);
    setUndoStack((stack) => stack.slice(0, -1));
    setMap(prev.map);
    setBackgroundMap(prev.backgroundMap);
    setForegroundMap(prev.foregroundMap);
    setSprites(prev.sprites);
    setEntities(prev.entities);
  }
  function redo() {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1]!;
    setUndoStack((stack) => [...stack, { map, backgroundMap, foregroundMap, sprites, entities }]);
    setRedoStack((stack) => stack.slice(0, -1));
    setMap(next.map);
    setBackgroundMap(next.backgroundMap);
    setForegroundMap(next.foregroundMap);
    setSprites(next.sprites);
    setEntities(next.entities);
  }
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (inEditableTarget() || event.repeat) return;
      const mod = event.ctrlKey || event.metaKey;
      if (mod) {
        if (event.code === 'KeyZ' && !event.shiftKey) {
          event.preventDefault();
          undo();
        } else if ((event.code === 'KeyZ' && event.shiftKey) || event.code === 'KeyY') {
          event.preventDefault();
          redo();
        }
        return;
      }
      // Tool hotkeys, matching the sprite/tilemap paint toolbar; entities
      // (Place) view has its own click-to-place interaction, no tool row.
      if (view === 'entities') return;
      if (event.code === 'KeyB') setTool('pencil');
      else if (event.code === 'KeyE') setTool('eraser');
      else if (event.code === 'KeyG') setTool('fill');
      else if (event.code === 'KeyI') setTool('pick');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  // Persistence: hydrate once from the stored document, then save edits
  // wholesale (debounced in the hook). Tool/tile stay session-local.
  const { projectId } = useEditorHost();
  const { project, setProject } = useProject();
  const [mapSyncState, setMapSyncState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const { data: storedDoc, setData: saveDoc, loading: docLoading } = useModeDocument<PixelDoc>('pixel', PIXEL_DOC_FALLBACK);

  // The active sprite, clip, and frame are the editing surface everywhere
  // below. A sprite always has at least one clip (blankSprite/migration
  // guarantee this), so activeClipName always resolves to a real clip.
  const spr = sprites[active] ?? Object.values(sprites)[0] ?? blankSprite(24);
  const clipNames = Object.keys(spr.clips);
  const activeClipName = spr.clips[spr.activeClip] ? spr.activeClip : clipNames[0]!;
  const clip = spr.clips[activeClipName]!;
  const frameIndex = Math.min(frame, clip.frames.length - 1);
  const size = spr.size;
  const grid = clip.frames[frameIndex] ?? clip.frames[0]!;
  const patchSprite = (name: string, patch: (s: PixelSpriteDoc) => PixelSpriteDoc) =>
    setSprites((all) => (all[name] ? { ...all, [name]: patch(all[name]!) } : all));
  const patchClip = (patch: (c: PixelClip) => PixelClip) =>
    patchSprite(active, (s) => ({ ...s, clips: { ...s.clips, [activeClipName]: patch(s.clips[activeClipName]!) } }));
  const setFrameCells = (update: (cells: (string | null)[]) => (string | null)[]) =>
    patchClip((c) => ({ ...c, frames: c.frames.map((f, i) => (i === frameIndex ? update(f) : f)) }));

  // In-editor animation preview: previously the only way to see a clip
  // actually animate at its set FPS was to leave Pixel Studio and go to
  // Preview. Steps through this clip's own frames at its own fps/loop
  // settings, so what plays here is exactly what the runtime will show.
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (!playing || clip.frames.length <= 1) return;
    const id = window.setInterval(() => {
      setFrame((f) => {
        const next = f + 1;
        if (next < clip.frames.length) return next;
        if (!clip.loop) { setPlaying(false); return f; }
        return 0;
      });
    }, 1000 / clip.fps);
    return () => window.clearInterval(id);
  }, [playing, clip.frames.length, clip.fps, clip.loop]);
  useEffect(() => setPlaying(false), [active, activeClipName]);

  // Compile the authored sprites/tiles/entities into the map document's
  // Tilemap2D so the runtime (Edit, Preview, and published play) renders this
  // content when the project's projection is 2D. Every sprite rides along;
  // the chosen player sprite drives the player flipbook.
  function buildTilemapLayer(): Tilemap2D {
    return {
      width: mapWidth,
      height: mapHeight,
      cells: map,
      // Each tile's baked art (if any) lives as a pseudo-sprite in `sprites`
      // (see tileArtSpriteKey) while being authored; compile it onto the
      // tile's own `art` field here, keyed by the tile's plain id.
      tiles: tiles.map((t) => {
        const artDoc = sprites[tileArtSpriteKey(t.id)];
        return artDoc ? { ...t, art: compileSpriteDoc(t.id, artDoc) } : t;
      }),
      playerSprite: spriteWire(sprites, player),
      sprites: Object.keys(sprites).filter((name) => !isTileArtSpriteKey(name)).map((name) => spriteWire(sprites, name)),
      entities: entities.map((e) => ({ id: e.id, spriteId: e.spriteId, x: e.x, y: e.y, blocking: e.blocking, kind: e.kind, ...(e.patrol ? { patrol: e.patrol } : {}) })),
      mode,
      ...(spawn ? { spawn } : {}),
      triggers: triggers.map((t) => ({
        id: t.id,
        center: [t.x + t.w / 2, t.y + t.h / 2],
        size: [t.w, t.h],
        kind: t.kind,
        ...(t.eventName ? { eventName: t.eventName } : {}),
        ...(t.targetX !== undefined && t.targetY !== undefined ? { targetPosition: [t.targetX, t.targetY] } : {}),
      })),
      ...(backgroundMap.some((c) => c !== null) ? { backgroundCells: backgroundMap } : {}),
      ...(foregroundMap.some((c) => c !== null) ? { foregroundCells: foregroundMap } : {}),
    };
  }

  // Compiling into the map is a second, differently-shaped save alongside the
  // raw PixelDoc's own autosave above (useModeDocument's `saveDoc`) - it needs
  // its own debounced saver rather than useModeDocument's, but follows the
  // exact same policy: schedule on edit, flush on unmount/pagehide/tab-hide,
  // so a switch to Preview never leaves the compiled map stale. `project` is
  // read through a ref so the saver (created once per project) always writes
  // onto the latest project state, not a stale closure over an early render.
  const latestProject = useRef(project);
  latestProject.current = project;
  const mapSyncRef = useRef<DebouncedSaver<Tilemap2D> | null>(null);
  const notify = useToast();
  useEffect(() => {
    const saver = createDebouncedSaver<Tilemap2D>((layer) => {
      const nextMap = { ...latestProject.current.map, tilemap2d: layer };
      setMapSyncState('saving');
      saveMapDocument(projectId, nextMap)
        .then(() => {
          setProject({ ...latestProject.current, map: nextMap });
          setMapSyncState('saved');
          setTimeout(() => setMapSyncState((s) => (s === 'saved' ? 'idle' : s)), 2000);
        })
        .catch(() => {
          setMapSyncState('error');
          notify("Pixel Studio's map sync didn't save - your sprite and tile edits are still safe, but Preview and Play may be showing an older version.", 'error');
        });
    }, 1500);
    mapSyncRef.current = saver;
    const onPageHide = () => saver.flush();
    const onVisibilityChange = () => { if (document.visibilityState === 'hidden') saver.flush(); };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      saver.flush();
      if (mapSyncRef.current === saver) mapSyncRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one saver per (project, kind), matching useModeDocument's own lifecycle; setProject/notify are stable.
  }, [projectId]);

  // Manual "Save now": flushes the pending debounced sync immediately, for a
  // creator about to switch to Preview who doesn't want to wait out the
  // debounce window.
  function saveToAssetsNow() {
    mapSyncRef.current?.schedule(buildTilemapLayer());
    mapSyncRef.current?.flush();
  }

  const hydrated = useRef(false);
  useEffect(() => {
    if (docLoading || hydrated.current) return;
    hydrated.current = true;
    const doc = migratePixelDoc(storedDoc);
    setView(doc.view);
    setMap(doc.map);
    setBackgroundMap(doc.backgroundMap);
    setForegroundMap(doc.foregroundMap);
    setMapWidth(doc.mapWidth);
    setMapHeight(doc.mapHeight);
    // Prefer the map's own live Tilemap2D.mode when a layer has already
    // been saved - the same field Game Setup's picker reads and writes
    // (see its own hydration comment) - falling back to this document's
    // own stored value only for a project with no saved layer yet. Without
    // this, switching mode in Game Setup and reopening Pixel Studio showed
    // this toggle's stale, separately-persisted value, which "Save to
    // assets" would then silently write back over Game Setup's choice.
    setMode(project.map.tilemap2d?.mode ?? doc.mode);
    setColour(doc.colour);
    setPalettes(doc.palettes);
    setActivePal(doc.activePal);
    setSprites(doc.sprites);
    setPlayer(doc.player);
    setActive(doc.player);
    setFrame(0);
    setTiles(doc.tiles);
    setEntities(doc.entities);
    setSpawn(doc.spawn);
    setTriggers(doc.triggers);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrates once, on load (guarded by hydrated.current); project.map.tilemap2d?.mode is read for that one hydration, not tracked afterwards, matching Game Setup's own identical pattern (editor-panels.tsx).
  }, [docLoading, storedDoc]);
  useEffect(() => {
    if (docLoading || !hydrated.current) return;
    saveDoc({ view, map, backgroundMap, foregroundMap, mapWidth, mapHeight, mode, colour, palettes, activePal, sprites, player, tiles, entities, spawn, triggers });
  }, [view, map, backgroundMap, foregroundMap, mapWidth, mapHeight, mode, colour, palettes, activePal, sprites, player, tiles, entities, spawn, triggers, docLoading, saveDoc]);
  // Same trigger set, minus the fields that aren't part of the compiled
  // Tilemap2D (view/colour/palettes/activePal are pure editor state) - keeps
  // Preview/Play in sync with the authored content without a manual click.
  useEffect(() => {
    if (docLoading || !hydrated.current) return;
    mapSyncRef.current?.schedule(buildTilemapLayer());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- buildTilemapLayer closes over the same fields already listed as deps here; including it would re-run every render since it's redefined each render.
  }, [map, backgroundMap, foregroundMap, mapWidth, mapHeight, mode, sprites, player, tiles, entities, spawn, triggers, docLoading]);

  // Sprite library operations.
  const spriteNames = Object.keys(sprites);
  // Excludes tile-art pseudo-sprites (see tileArtSpriteKey): the Sprites
  // list, the player toggle, and the Place-mode sprite picker only ever deal
  // in real, placeable sprites.
  const realSpriteNames = spriteNames.filter((n) => !isTileArtSpriteKey(n));
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [pendingTileDelete, setPendingTileDelete] = useState<string | null>(null);
  const [pendingTileArtClear, setPendingTileArtClear] = useState<string | null>(null);
  const [tileArtImportError, setTileArtImportError] = useState<string | null>(null);
  const tileArtFileInputRef = useRef<HTMLInputElement>(null);
  const [renamingSprite, setRenamingSprite] = useState<string | null>(null);
  const [pendingPaletteDelete, setPendingPaletteDelete] = useState<number | null>(null);
  function deletePalette(index: number) {
    setPalettes((all) => all.filter((_, i) => i !== index));
    setActivePal((current) => (current >= index ? Math.max(0, current - 1) : current));
  }
  function addSprite() {
    let n = spriteNames.length + 1;
    while (sprites['sprite_' + n]) n += 1;
    const name = 'sprite_' + n;
    setSprites((all) => ({ ...all, [name]: blankSprite(size) }));
    setActive(name);
    setFrame(0);
  }
  function deleteSprite(name: string) {
    setSprites((all) => {
      const next = { ...all };
      delete next[name];
      return next;
    });
    // Placed instances of a deleted sprite would otherwise dangle: the
    // runtime skips them safely, but leaving them in the doc means they
    // silently reappear if a sprite of the same name is added back later.
    setEntities((all) => all.filter((e) => e.spriteId !== name));
    if (active === name) {
      setActive(player === name ? realSpriteNames.find((s) => s !== name) ?? player : player);
      setFrame(0);
    }
  }
  // Custom sprite ids are permanent otherwise, stuck as the auto-generated
  // sprite_2/sprite_3 they were created with; this is what ships in exported
  // code and the runtime. Renaming carries every reference along: the
  // player/active selection, placed entities, and a live sync target.
  function renameSprite(oldName: string, newName: string) {
    const next = newName.trim();
    if (!next || next === oldName || sprites[next] || isTileArtSpriteKey(next)) return;
    setSprites((all) => {
      const { [oldName]: doc, ...rest } = all;
      return doc ? { ...rest, [next]: doc } : all;
    });
    setEntities((all) => all.map((e) => (e.spriteId === oldName ? { ...e, spriteId: next } : e)));
    if (player === oldName) setPlayer(next);
    if (active === oldName) setActive(next);
    if (syncedSprite === oldName) setSyncedSprite(next);
    if (placeSprite === oldName) setPlaceSprite(next);
  }

  // Code round trip: "Export to code" downloads the active sprite as
  // deterministic JSON; "Open in Studio" reads one back in. Importing a
  // sprite whose id already exists in this project asks first, rather than
  // silently discarding whatever is currently there.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [pendingImport, setPendingImport] = useState<{ id: string; sprite: PixelSpriteDoc } | null>(null);
  function downloadSpriteCode(name: string, sprite: PixelSpriteDoc) {
    // A tile-art pseudo-sprite's internal key never leaks into the exported
    // file: the id and filename always use the plain tile id.
    const cleanName = isTileArtSpriteKey(name) ? name.slice(TILE_ART_SPRITE_PREFIX.length) : name;
    const text = exportSpriteToCode(cleanName, sprite);
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = cleanName + '.sprite.json';
    a.click();
    URL.revokeObjectURL(url);
  }
  async function importSpriteCodeFile(file: File) {
    try {
      const text = await file.text();
      const { id, sprite } = parseSpriteCode(text);
      if (sprites[id]) {
        setPendingImport({ id, sprite });
      } else {
        setSprites((all) => ({ ...all, [id]: sprite }));
        setActive(id);
        setFrame(0);
      }
      setImportError(null);
    } catch (err) {
      setImportError(err instanceof SpriteCodeError ? err.message : 'Could not read that file.');
    }
  }

  // Grid-slice PNG/spritesheet import (R16): distinct from the .sprite.json
  // round trip above - this brings in externally authored pixel art, sliced
  // into one or more new sprites at a creator-chosen cell size.
  const [spritesheetImportOpen, setSpritesheetImportOpen] = useState(false);
  function importSlicedSprites(imported: { name: string; sprite: PixelSpriteDoc }[]) {
    if (imported.length === 0) return;
    pushUndoSnapshot();
    setSprites((all) => {
      const next = { ...all };
      for (const { name, sprite } of imported) next[name] = sprite;
      return next;
    });
    setActive(imported[0]!.name);
    setFrame(0);
    setSpritesheetImportOpen(false);
  }

  // Live sync (dev only, see scripts/spriteSyncPlugin.ts): watch a local
  // .sprite.json file and stay in sync with it over Vite's HMR channel.
  // import.meta.hot is undefined in production builds, so this whole
  // feature is simply absent there - no separate feature flag needed.
  const [syncPath, setSyncPath] = useState('');
  const trimmedSyncPath = syncPath.trim();
  const [syncedSprite, setSyncedSprite] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'watching' | 'error'>('idle');
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncedAt, setSyncedAt] = useState<number | null>(null);
  // Mirrors the server's own echo guard: skip writing back content that's
  // already exactly what we last saw on disk, so receiving a change never
  // immediately triggers a redundant, id-renaming write straight back out.
  const lastSyncedTextRef = useRef<string | null>(null);

  useEffect(() => {
    if (!import.meta.hot || !syncedSprite) return;
    const hot = import.meta.hot;
    const onWatching = (data: { path: string; text?: string }) => {
      if (data.path !== trimmedSyncPath) return;
      setSyncStatus('watching');
      setSyncMessage(null);
      if (data.text === undefined) return;
      try {
        const { sprite } = parseSpriteCode(data.text);
        lastSyncedTextRef.current = data.text;
        setSprites((all) => ({ ...all, [syncedSprite]: sprite }));
        setSyncedAt(Date.now());
      } catch (err) {
        setSyncStatus('error');
        setSyncMessage(err instanceof SpriteCodeError ? err.message : 'Could not read the synced file.');
      }
    };
    const onChanged = (data: { path: string; text: string }) => {
      if (data.path !== trimmedSyncPath) return;
      try {
        const { sprite } = parseSpriteCode(data.text);
        lastSyncedTextRef.current = data.text;
        setSprites((all) => ({ ...all, [syncedSprite]: sprite }));
        setSyncedAt(Date.now());
      } catch (err) {
        setSyncStatus('error');
        setSyncMessage(err instanceof SpriteCodeError ? err.message : 'Could not read the synced file.');
      }
    };
    const onError = (data: { message: string }) => {
      setSyncStatus('error');
      setSyncMessage(data.message);
    };
    hot.on('sprite-sync:watching', onWatching);
    hot.on('sprite-sync:changed', onChanged);
    hot.on('sprite-sync:error', onError);
    return () => {
      hot.off('sprite-sync:watching', onWatching);
      hot.off('sprite-sync:changed', onChanged);
      hot.off('sprite-sync:error', onError);
    };
  }, [trimmedSyncPath, syncedSprite]);

  // Debounced write-back: local edits to the watched sprite push out to
  // disk 1500ms after the last change, matching useModeDocument's own
  // debounce so this doesn't establish a different save rhythm.
  useEffect(() => {
    if (!import.meta.hot || !syncedSprite || syncStatus !== 'watching') return;
    const sprite = sprites[syncedSprite];
    if (!sprite) return;
    const text = exportSpriteToCode(syncedSprite, sprite);
    if (text === lastSyncedTextRef.current) return;
    const hot = import.meta.hot;
    const timer = setTimeout(() => {
      lastSyncedTextRef.current = text;
      hot.send('sprite-sync:write', { path: trimmedSyncPath, text });
      setSyncedAt(Date.now());
    }, 1500);
    return () => clearTimeout(timer);
  }, [sprites, syncedSprite, syncStatus, trimmedSyncPath]);

  function startSync() {
    if (!import.meta.hot || !trimmedSyncPath) return;
    lastSyncedTextRef.current = null;
    setSyncedSprite(active);
    setSyncStatus('idle');
    setSyncMessage(null);
    import.meta.hot.send('sprite-sync:watch', { path: trimmedSyncPath });
  }
  function stopSync() {
    if (import.meta.hot && trimmedSyncPath) import.meta.hot.send('sprite-sync:unwatch', { path: trimmedSyncPath });
    setSyncedSprite(null);
    setSyncStatus('idle');
    setSyncMessage(null);
  }

  // Frame operations (the flipbook strip), scoped to the active clip.
  function addFrame() {
    patchClip((c) => ({ ...c, frames: [...c.frames, c.frames[frameIndex]!.slice()] }));
    setFrame(clip.frames.length);
  }
  function deleteFrame() {
    if (clip.frames.length <= 1) return;
    patchClip((c) => ({ ...c, frames: c.frames.filter((_, i) => i !== frameIndex) }));
    setFrame(Math.max(0, frameIndex - 1));
  }

  // Clip operations (the clip list). A sprite always keeps at least one clip.
  function addClip() {
    let n = clipNames.length + 1;
    while (spr.clips['clip_' + n]) n += 1;
    const clipName = 'clip_' + n;
    patchSprite(active, (s) => ({
      ...s,
      clips: { ...s.clips, [clipName]: { frames: [Array(s.size * s.size).fill(null)], fps: 8, loop: true } },
      activeClip: clipName,
    }));
    setFrame(0);
  }
  function duplicateClip(name: string) {
    const source = spr.clips[name];
    if (!source) return;
    let n = 2;
    let clipName = name + '_' + n;
    while (spr.clips[clipName]) { n += 1; clipName = name + '_' + n; }
    patchSprite(active, (s) => ({
      ...s,
      clips: { ...s.clips, [clipName]: { frames: source.frames.map((f) => f.slice()), fps: source.fps, loop: source.loop } },
      activeClip: clipName,
    }));
    setFrame(0);
  }
  function renameClip(oldName: string, newName: string) {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName || spr.clips[trimmed]) return;
    patchSprite(active, (s) => {
      const moved = s.clips[oldName];
      if (!moved) return s;
      const rest = { ...s.clips };
      delete rest[oldName];
      return { ...s, clips: { ...rest, [trimmed]: moved }, activeClip: s.activeClip === oldName ? trimmed : s.activeClip };
    });
  }
  function deleteClip(name: string) {
    if (clipNames.length <= 1) return;
    patchSprite(active, (s) => {
      if (Object.keys(s.clips).length <= 1) return s;
      const rest = { ...s.clips };
      delete rest[name];
      return { ...s, clips: rest, activeClip: s.activeClip === name ? Object.keys(rest)[0]! : s.activeClip };
    });
    setFrame(0);
  }
  function reorderClip(name: string, direction: -1 | 1) {
    const idx = clipNames.indexOf(name);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= clipNames.length) return;
    const order = clipNames.slice();
    const tmp = order[idx]!;
    order[idx] = order[swapIdx]!;
    order[swapIdx] = tmp;
    patchSprite(active, (s) => ({ ...s, clips: Object.fromEntries(order.map((n) => [n, s.clips[n]!])) }));
  }

  // Resizing keeps the art (top-left region) across every frame of every
  // clip; shrinking that would crop painted cells in ANY clip asks first,
  // not just the one currently being viewed.
  const [pendingResize, setPendingResize] = useState<number | null>(null);
  function applyResize(n: number) {
    patchSprite(active, (s) => resizeSpriteDoc(s, n));
  }
  function resize(n: number) {
    if (n === size) return;
    if (spriteResizeLosesArt(spr, n)) {
      setPendingResize(n);
      return;
    }
    applyResize(n);
  }

  function paintSprite(i: number) {
    if (playing) return;
    if (tool === 'pick') {
      const picked = grid[i];
      if (picked) setColour(picked);
      return;
    }
    if (!painting.current) pushUndoSnapshot();
    setFrameCells((g) => {
      const next = g.slice();
      if (tool === 'pencil') next[i] = colour;
      else if (tool === 'eraser') next[i] = null;
      else if (tool === 'fill') {
        const target = g[i];
        if (target === colour) return g;
        const q = [i];
        const seen = new Set([i]);
        while (q.length) {
          const p = q.pop();
          if (p === undefined) break;
          if (next[p] !== target) continue;
          next[p] = colour;
          const x = p % size, y = (p / size) | 0;
          const neighbours: [number, number][] = [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]];
          neighbours.forEach(([nx, ny]) => {
            if (nx >= 0 && nx < size && ny >= 0 && ny < size) {
              const np = ny * size + nx;
              if (!seen.has(np)) { seen.add(np); q.push(np); }
            }
          });
        }
      }
      return next;
    });
  }

  function paintMap(i: number) {
    if (!painting.current) pushUndoSnapshot();
    const setLayer = mapLayer === 'background' ? setBackgroundMap : mapLayer === 'foreground' ? setForegroundMap : setMap;
    setLayer((m) => {
      const next = m.slice();
      next[i] = tool === 'eraser' ? null : tile;
      return next;
    });
  }

  // Map resize: same shrink-loses-art confirm pattern as sprite resize,
  // checked across all three layers since any of them may hold painted art.
  const [pendingMapResize, setPendingMapResize] = useState<{ w: number; h: number } | null>(null);
  function applyMapResize(w: number, h: number) {
    setMap((m) => resizeTilemapGrid(m, mapWidth, mapHeight, w, h));
    setBackgroundMap((m) => resizeTilemapGrid(m, mapWidth, mapHeight, w, h));
    setForegroundMap((m) => resizeTilemapGrid(m, mapWidth, mapHeight, w, h));
    // Entities outside the new bounds have nowhere to live; the rest keep
    // their cell exactly (top-left-anchored resize, same as the grid).
    setEntities((all) => all.filter((e) => e.x < w && e.y < h));
    setMapWidth(w);
    setMapHeight(h);
  }
  function resizeMap(w: number, h: number) {
    if (w === mapWidth && h === mapHeight) return;
    const losesArt = tilemapResizeLosesArt(map, mapWidth, mapHeight, w, h)
      || tilemapResizeLosesArt(backgroundMap, mapWidth, mapHeight, w, h)
      || tilemapResizeLosesArt(foregroundMap, mapWidth, mapHeight, w, h);
    if (losesArt) {
      setPendingMapResize({ w, h });
      return;
    }
    applyMapResize(w, h);
  }

  // Custom tiles: a project starts with the built-in set and can add more.
  // Ids are namespaced tile_N to guarantee uniqueness without asking the
  // creator to pick one up front.
  function addTile() {
    let n = tiles.length + 1;
    while (tiles.some((t) => t.id === 'tile_' + n)) n += 1;
    const id = 'tile_' + n;
    setTiles((all) => [...all, { id, a: colour, b: colour, solid: false }]);
    setTile(id);
  }
  function patchTile(id: string, patch: Partial<PixelTile>) {
    setTiles((all) => all.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }
  // Keeps `solid` in step with `kind` from one choice, since the top-down
  // runtime (topDown.ts) only ever reads `solid` and has no notion of the
  // richer platformer kinds.
  function patchTileCollision(id: string, kind: TileCollisionKind) {
    patchTile(id, { kind, solid: kind !== 'none' });
  }
  function deleteTile(id: string) {
    setTiles((all) => all.filter((t) => t.id !== id));
    setMap((m) => m.map((c) => (c === id ? null : c)));
    setBackgroundMap((m) => m.map((c) => (c === id ? null : c)));
    setForegroundMap((m) => m.map((c) => (c === id ? null : c)));
    setSprites((all) => withoutTileArtSprite(all, id));
    if (tile === id) setTile(tiles.find((t) => t.id !== id)?.id ?? 'grass');
    if (active === tileArtSpriteKey(id)) { setActive(player); setFrame(0); }
  }
  // Custom tile ids are permanent otherwise, stuck as the auto-generated
  // tile_2/tile_3 they were created with even after every cell painted with
  // them is repainted; this is what ships in exported code and the runtime.
  function renameTile(oldId: string, newId: string) {
    const next = newId.trim();
    if (!next || next === oldId || tiles.some((t) => t.id === next)) return;
    setTiles((all) => all.map((t) => (t.id === oldId ? { ...t, id: next } : t)));
    setMap((m) => m.map((c) => (c === oldId ? next : c)));
    setBackgroundMap((m) => m.map((c) => (c === oldId ? next : c)));
    setForegroundMap((m) => m.map((c) => (c === oldId ? next : c)));
    setSprites((all) => renameTileArtSprite(all, oldId, next));
    if (tile === oldId) setTile(next);
    if (active === tileArtSpriteKey(oldId)) setActive(tileArtSpriteKey(next));
  }

  // Tile baked art: open/export/import/clear a tile's `art` field via the
  // exact same sprite canvas and .sprite.json round trip every other sprite
  // already uses (see tileArtSpriteKey) - no second editing surface, no
  // second file format.
  function openTileArtInStudio(tileId: string) {
    const key = tileArtSpriteKey(tileId);
    setSprites((all) => (all[key] ? all : { ...all, [key]: blankSprite(16) }));
    setView('sprite');
    setActive(key);
    setFrame(0);
  }
  function exportTileArt(tileId: string) {
    const art = sprites[tileArtSpriteKey(tileId)];
    if (art) downloadSpriteCode(tileId, art);
  }
  async function importTileArtFile(tileId: string, file: File) {
    try {
      const text = await file.text();
      // Writes into this tile's reserved slot regardless of whatever id the
      // file itself declares, the same way the live-sync watcher below
      // writes into its known synced slot rather than trusting the file.
      const { sprite } = parseSpriteCode(text);
      setSprites((all) => ({ ...all, [tileArtSpriteKey(tileId)]: sprite }));
      setTileArtImportError(null);
    } catch (err) {
      setTileArtImportError(err instanceof SpriteCodeError ? err.message : 'Could not read that file.');
    }
  }
  function clearTileArt(tileId: string) {
    setSprites((all) => withoutTileArtSprite(all, tileId));
    if (active === tileArtSpriteKey(tileId)) { setActive(player); setFrame(0); }
  }

  // Entity placement: click an empty cell to place the selected sprite,
  // click an occupied one to remove whatever's there. Dragging repeats
  // whichever action the first cell did (place across empty cells, or erase
  // across occupied ones) rather than re-toggling each cell it crosses, the
  // same distinction pencil vs eraser makes on the pixel/tile canvases -
  // one entity per cell keeps the interaction a single click instead of a
  // stack/z-order concept.
  const [placeSprite, setPlaceSprite] = useState('player_idle');
  // Falls back to any real sprite if the chosen one was since renamed/deleted,
  // rather than silently placing a dangling reference.
  const effectivePlaceSprite = sprites[placeSprite] ? placeSprite : (realSpriteNames[0] ?? player);
  const placeStrokeMode = useRef<'place' | 'remove'>('place');
  function placeEntity(i: number, continuing = false) {
    if (!continuing) pushUndoSnapshot();
    const x = i % mapWidth;
    const y = (i / mapWidth) | 0;
    const existing = entities.find((e) => e.x === x && e.y === y);
    const mode = continuing ? placeStrokeMode.current : (existing ? 'remove' : 'place');
    if (!continuing) placeStrokeMode.current = mode;
    if (mode === 'place') {
      if (existing) return;
      const id = 'entity_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      setEntities((all) => [...all, { id, spriteId: effectivePlaceSprite, x, y, blocking: placeBlocking, kind: placeKind }]);
    } else if (existing) {
      setEntities((all) => all.filter((e) => e.id !== existing.id));
    }
  }

  // Trigger placement: same click-to-place/click-to-remove interaction as
  // entities above, but keyed on the placeTool toggle - a click while
  // "Trigger" is selected never touches `entities`, and vice versa. Unlike
  // entities, a trigger has a multi-cell footprint (`w`/`h`, configured
  // before placing, in the panel below), so "occupied" means any cell inside
  // that footprint, not just an exact (x, y) match. No drag-to-place-many
  // (each placement's footprint is easy to misjudge while dragging), so
  // there's no `continuing` parameter to mirror `placeEntity`'s.
  function triggerAt(x: number, y: number): PixelTriggerDoc | undefined {
    return triggers.find((t) => x >= t.x && x < t.x + t.w && y >= t.y && y < t.y + t.h);
  }
  function placeOrRemoveTrigger(i: number) {
    const x = i % mapWidth;
    const y = (i / mapWidth) | 0;
    const existing = triggerAt(x, y);
    if (existing) {
      setTriggers((all) => all.filter((t) => t.id !== existing.id));
      return;
    }
    const id = 'trigger_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    setTriggers((all) => [
      ...all,
      {
        id,
        x,
        y,
        w: Math.max(1, triggerW),
        h: Math.max(1, triggerH),
        kind: triggerKind,
        ...(triggerEventName ? { eventName: triggerEventName } : {}),
        ...(triggerKind === 'teleport' ? { targetX: triggerTargetX, targetY: triggerTargetY } : {}),
      },
    ]);
  }

  // Patrol authoring: a placed npc-kind entity gains an optional waypoint
  // route. Selection is a dropdown rather than a viewport click, since a
  // click on a placed entity already means "remove it" in Place mode above -
  // adding a second click meaning would make that interaction ambiguous.
  const [patrolEntityId, setPatrolEntityId] = useState<string | null>(null);
  const npcEntities = entities.filter((e) => e.kind === 'npc');
  const patrolEntity = npcEntities.find((e) => e.id === patrolEntityId) ?? null;
  function patchPatrol(id: string, patch: (p: WaypointPatrol) => WaypointPatrol) {
    setEntities((all) => all.map((e) => {
      if (e.id !== id) return e;
      const current = e.patrol ?? { points: [], loopMode: 'loop', speed: 3 };
      return { ...e, patrol: patch(current) };
    }));
  }
  function addWaypoint(id: string, x: number, y: number) {
    patchPatrol(id, (p) => ({ ...p, points: [...p.points, { x, y, dwellMs: 0 }] }));
  }
  function updateWaypoint(id: string, index: number, patch: Partial<{ x: number; y: number; dwellMs: number }>) {
    patchPatrol(id, (p) => ({ ...p, points: p.points.map((pt, i) => (i === index ? { ...pt, ...patch } : pt)) }));
  }
  function removeWaypoint(id: string, index: number) {
    patchPatrol(id, (p) => ({ ...p, points: p.points.filter((_, i) => i !== index) }));
  }
  function removePatrol(id: string) {
    setEntities((all) => all.map((e) => (e.id === id ? { ...e, patrol: undefined } : e)));
  }

  const mapCell = 26;
  // A tile-art pseudo-sprite's internal key never leaks into the on-canvas
  // label either - shown as "tile:<id>" instead of the raw "@tile:<id>".
  const displayActive = isTileArtSpriteKey(active) ? 'tile:' + active.slice(TILE_ART_SPRITE_PREFIX.length) : active;
  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      {/* left: sprites + canvas settings */}
      <M2Panel side="left" width={232}>
        <M2Label>Mode</M2Label>
        <TabStrip
          listLabel="Pixel Studio mode"
          variant="segmented"
          tabs={PIXEL_VIEW_TABS}
          activeId={view}
          onSelect={(id) => setView(id as PixelView)}
          style={{ margin: '0 16px' }}
        />
        {view === 'sprite' && (
          <Fragment>
            {isTileArtSpriteKey(active) && (
              <div style={{ margin: '0 16px 4px', padding: '10px 12px', borderRadius: 9, background: 'var(--ss-accent-soft)', border: '1px solid var(--ss-accent-border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 'var(--ss-fs-xs)', fontWeight: 500, color: 'var(--ss-accent)' }}>Editing baked art for tile &ldquo;{active.slice(TILE_ART_SPRITE_PREFIX.length)}&rdquo;</span>
                <button className="ss-btn ss-btn--ghost" onClick={() => setView('tilemap')} style={{ alignSelf: 'flex-start', padding: '4px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>Done - back to tilemap</button>
              </div>
            )}
            <M2Label action={<button className="ss-btn ss-btn--ghost" onClick={addSprite} style={{ padding: '3px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>+ New</button>}>Sprites</M2Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
              {realSpriteNames.map((s) => (
                <M2ListRow
                  key={s}
                  active={active === s}
                  interactive={renamingSprite !== s}
                  onClick={() => { setActive(s); setFrame(0); }}
                  onDoubleClick={() => setRenamingSprite(s)}
                  title="Double-click to rename"
                  extra={
                    <button
                      onClick={() => setPlayer(s)}
                      title={player === s ? 'The 2D player sprite' : 'Make this the 2D player sprite'}
                      aria-label={'Player sprite: ' + s}
                      style={{ border: 'none', background: 'transparent', padding: '4px 6px', fontSize: 'var(--ss-fs-xs)', color: player === s ? 'var(--ss-accent)' : 'var(--ss-text-3)', opacity: player === s ? 1 : 0.5, flexShrink: 0 }}
                    >{player === s ? '●' : '○'}</button>
                  }
                  onDelete={s !== player ? () => setPendingDelete(s) : undefined}
                  deleteLabel={'Delete ' + s}
                >
                  {renamingSprite === s ? (
                    <input
                      className="ss-input"
                      autoFocus
                      defaultValue={s}
                      title="Renaming also updates every place this sprite is referenced (player, placed entities, live sync)"
                      onBlur={(e) => { renameSprite(s, e.target.value); setRenamingSprite(null); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        else if (e.key === 'Escape') setRenamingSprite(null);
                      }}
                      style={{ flex: 1, minWidth: 0, margin: '3px 6px', padding: '4px 8px', fontSize: 'var(--ss-fs-sm)', borderRadius: 6, fontFamily: 'var(--ss-mono)' }}
                    />
                  ) : (
                    <>{s}{sprites[s] ? spriteSummaryLabel(sprites[s]!) : ''}</>
                  )}
                </M2ListRow>
              ))}
            </div>
            <div style={{ padding: '8px 16px 0', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
              The ● sprite plays as the 2D player. Frames below the canvas turn a sprite into a flipbook.
            </div>
            <M2Label>Canvas</M2Label>
            <TabStrip
              listLabel="Sprite canvas size"
              variant="segmented"
              tabs={PIXEL_CANVAS_SIZE_TABS}
              activeId={String(size)}
              onSelect={(id) => resize(Number(id))}
              style={{ margin: '0 16px' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '8px 16px 0', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
              Custom
              <input className="ss-input" type="number" min={4} max={128} value={size}
                onChange={(e) => { const v = Math.round(Number(e.target.value)); if (Number.isFinite(v) && v >= 4 && v <= 128) resize(v); }}
                style={{ width: 56, padding: '5px 7px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, textAlign: 'center' }} />
              <span>px</span>
            </label>

            <M2Label action={<button className="ss-btn ss-btn--ghost" onClick={addClip} style={{ padding: '3px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>+ New</button>}>Clips</M2Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
              {clipNames.map((name, i) => (
                <M2ListRow
                  key={name}
                  active={name === activeClipName}
                  onClick={() => { patchSprite(active, (s) => ({ ...s, activeClip: name })); setFrame(0); }}
                  labelStyle={{ padding: '6px 10px', fontSize: 'var(--ss-fs-xs)' }}
                  extra={
                    <>
                      <button onClick={() => reorderClip(name, -1)} disabled={i === 0} title="Move earlier" aria-label={'Move ' + name + ' earlier'}
                        style={{ border: 'none', background: 'transparent', padding: '2px 2px', fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', opacity: i === 0 ? 0.3 : 1, flexShrink: 0 }}>↑</button>
                      <button onClick={() => reorderClip(name, 1)} disabled={i === clipNames.length - 1} title="Move later" aria-label={'Move ' + name + ' later'}
                        style={{ border: 'none', background: 'transparent', padding: '2px 2px', fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', opacity: i === clipNames.length - 1 ? 0.3 : 1, flexShrink: 0 }}>↓</button>
                      <button onClick={() => duplicateClip(name)} title={'Duplicate ' + name} aria-label={'Duplicate ' + name}
                        style={{ border: 'none', background: 'transparent', padding: '2px 5px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', flexShrink: 0 }}>⧉</button>
                    </>
                  }
                  onDelete={clipNames.length > 1 ? () => deleteClip(name) : undefined}
                  deleteLabel={'Delete ' + name}
                >
                  {name} · {spr.clips[name]!.frames.length}f
                </M2ListRow>
              ))}
            </div>
            <div style={{ padding: '6px 16px 0' }}>
              <input className="ss-input" list="pose-id-suggestions" defaultValue={activeClipName} key={activeClipName}
                onChange={(e) => renameClip(activeClipName, e.target.value)}
                placeholder="Clip name (e.g. idle, run, jump)"
                style={{ width: '100%', padding: '6px 9px', fontSize: 'var(--ss-fs-xs)', borderRadius: 8 }} />
              <datalist id="pose-id-suggestions">
                {POSE_ID_SUGGESTIONS.map((p) => <option key={p} value={p} />)}
              </datalist>
              <div style={{ marginTop: 4, fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', lineHeight: 1.4 }}>
                Name a clip after a suggested pose so the 2D player plays it automatically; any other name works too, for extra one-off clips.
              </div>
            </div>

            <M2Label>Code</M2Label>
            <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button className="ss-btn ss-btn--ghost" onClick={() => downloadSpriteCode(active, spr)} style={{ padding: '6px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>Export to code</button>
              <button className="ss-btn ss-btn--ghost" onClick={() => fileInputRef.current?.click()} style={{ padding: '6px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>Open in Studio</button>
              <input ref={fileInputRef} type="file" accept=".json,application/json" style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void importSpriteCodeFile(f); e.target.value = ''; }} />
              {importError && <div style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-danger)', lineHeight: 1.4 }}>{importError}</div>}
            </div>

            <M2Label>Import art</M2Label>
            <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button className="ss-btn ss-btn--ghost" onClick={() => setSpritesheetImportOpen(true)} style={{ padding: '6px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>
                Import PNG / spritesheet…
              </button>
              <div style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', lineHeight: 1.4 }}>
                Bring in existing pixel art: slices an image into new sprites at a cell size you choose.
              </div>
            </div>

            {import.meta.env.DEV && (
              <Fragment>
                <M2Label>Live sync (dev)</M2Label>
                <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {syncedSprite && syncedSprite !== active ? (
                    <div style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
                      Currently syncing &ldquo;{syncedSprite}&rdquo;.
                      <button className="ss-btn ss-btn--ghost" onClick={stopSync} style={{ marginLeft: 6, padding: '2px 8px', fontSize: 'var(--ss-fs-2xs)', borderRadius: 6 }}>Stop</button>
                    </div>
                  ) : (
                    <Fragment>
                      <input className="ss-input" value={syncPath} onChange={(e) => setSyncPath(e.target.value)}
                        placeholder="../../ninelives/sstk-ninelives/src/game/cat.sprite.json"
                        disabled={syncedSprite === active}
                        style={{ padding: '6px 9px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, fontFamily: 'var(--ss-mono)' }} />
                      {syncedSprite === active ? (
                        <button className="ss-btn ss-btn--ghost" onClick={stopSync} style={{ padding: '6px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>Stop watching</button>
                      ) : (
                        <button className="ss-btn ss-btn--ghost" onClick={startSync} disabled={!trimmedSyncPath} style={{ padding: '6px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>Watch this file</button>
                      )}
                      <div style={{ fontSize: 'var(--ss-fs-2xs)', color: syncStatus === 'error' ? 'var(--ss-danger)' : 'var(--ss-text-3)', lineHeight: 1.4 }}>
                        {syncStatus === 'error'
                          ? (syncMessage ?? 'Sync error')
                          : syncedSprite === active
                            ? (syncStatus === 'watching' ? 'Watching' + (syncedAt ? ' · last synced ' + new Date(syncedAt).toLocaleTimeString() : '') : 'Connecting…')
                            : 'Edit the file on disk or here; each updates the other. Dev server only, never in the deployed app.'}
                      </div>
                    </Fragment>
                  )}
                </div>
              </Fragment>
            )}
          </Fragment>
        )}
        {view === 'tilemap' && (
          <Fragment>
            <M2Label>Movement</M2Label>
            <TabStrip
              listLabel="Map movement model"
              variant="segmented"
              tabs={PIXEL_MODE_TABS}
              activeId={mode}
              onSelect={(id) => setMode(id as PixelMapMode)}
              style={{ margin: '0 16px' }}
            />
            <div style={{ padding: '8px 16px 0', fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', lineHeight: 1.4 }}>
              Top-down: flat movement, seen from above. Platformer: side-scrolling with gravity, jumping and the platformer-only collision kinds below.
            </div>
            <M2Label>Layer</M2Label>
            <TabStrip
              listLabel="Tilemap paint layer"
              variant="segmented"
              tabs={PIXEL_LAYER_TABS}
              activeId={mapLayer}
              onSelect={(id) => setMapLayer(id as PixelMapLayer)}
              style={{ margin: '0 16px' }}
            />
            <div style={{ padding: '8px 16px 0', fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', lineHeight: 1.4 }}>
              Background paints behind Main; Foreground paints in front of the player. The other two layers show dimmed as reference.
            </div>
            <M2Label action={<button className="ss-btn ss-btn--ghost" onClick={addTile} style={{ padding: '3px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>+ New</button>}>Tiles</M2Label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '0 16px' }}>
              {tiles.map((t) => (
                <button key={t.id} onClick={() => setTile(t.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: 6, borderRadius: 9,
                  border: '1px solid ' + (tile === t.id ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)'),
                  background: tile === t.id ? 'var(--ss-accent-soft)' : 'transparent',
                  color: tile === t.id ? 'var(--ss-accent)' : 'var(--ss-text-2)', fontSize: 'var(--ss-fs-xs)', fontWeight: 500,
                  overflow: 'hidden',
                }}>
                  <span style={{ borderRadius: 4, overflow: 'hidden', display: 'block', flexShrink: 0 }}><TileCell tile={t.id} size={20} tiles={tiles} /></span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.id}</span>
                </button>
              ))}
            </div>
            {(() => {
              const selected = tiles.find((t) => t.id === tile);
              if (!selected) return null;
              const builtIn = PIXEL_TILES.some((t) => t.id === selected.id);
              const tileArt = sprites[tileArtSpriteKey(selected.id)];
              const tileArtClip = tileArt ? (tileArt.clips[tileArt.activeClip] ?? Object.values(tileArt.clips)[0]) : undefined;
              const tileArtCells = tileArtClip?.frames[0];
              return (
                <Fragment>
                  <M2Label>Edit tile</M2Label>
                  <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {!builtIn && (
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
                        ID
                        <input
                          className="ss-input"
                          key={selected.id}
                          defaultValue={selected.id}
                          onBlur={(e) => renameTile(selected.id, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                          style={{ padding: '7px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 8, fontFamily: 'var(--ss-mono)' }}
                        />
                      </label>
                    )}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
                        Base
                        <input type="color" value={selected.a} onChange={(e) => patchTile(selected.id, { a: e.target.value })}
                          style={{ width: 28, height: 22, padding: 0, border: '1px solid var(--ss-border)', borderRadius: 5, background: 'none' }} />
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
                        Accent
                        <input type="color" value={selected.b} onChange={(e) => patchTile(selected.id, { b: e.target.value })}
                          style={{ width: 28, height: 22, padding: 0, border: '1px solid var(--ss-border)', borderRadius: 5, background: 'none' }} />
                      </label>
                    </div>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
                      Collision
                      <select
                        className="ss-input"
                        value={selected.kind ?? (selected.solid ? 'solid' : 'none')}
                        onChange={(e) => patchTileCollision(selected.id, e.target.value as TileCollisionKind)}
                        style={{ appearance: 'none', padding: '7px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 8 }}
                      >
                        {TILE_COLLISION_KINDS.map((k) => {
                          const inactive = mode !== 'platformer' && PLATFORMER_ONLY_COLLISION_KINDS.has(k.value);
                          return (
                            <option key={k.value} value={k.value} disabled={inactive}>
                              {k.label}{inactive ? ' - platformer only' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </label>
                    {mode !== 'platformer' && PLATFORMER_ONLY_COLLISION_KINDS.has(selected.kind ?? 'none') && (
                      <span style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-danger)', lineHeight: 1.4 }}>
                        This tile&rsquo;s collision kind only has an effect in Platformer mode - switch Movement above, or pick a different kind.
                      </span>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4, paddingTop: 10, borderTop: '1px solid var(--ss-border-soft)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ borderRadius: 4, overflow: 'hidden', display: 'flex', flexShrink: 0, border: '1px solid var(--ss-border-soft)' }}>
                          {tileArt && tileArtCells ? <FrameThumb cells={tileArtCells} size={tileArt.size} /> : <TileCell tile={selected.id} size={20} tiles={tiles} />}
                        </span>
                        <span style={{ fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>{tileArt ? 'Has baked pixel art' : 'No baked art - falls back to base/accent colours'}</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        <button className="ss-btn ss-btn--ghost" onClick={() => openTileArtInStudio(selected.id)} style={{ padding: '5px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>Open art in Studio</button>
                        {tileArt && (
                          <button className="ss-btn ss-btn--ghost" onClick={() => exportTileArt(selected.id)} style={{ padding: '5px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>Export tile art</button>
                        )}
                        <button className="ss-btn ss-btn--ghost" onClick={() => tileArtFileInputRef.current?.click()} style={{ padding: '5px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>Import tile art</button>
                        {tileArt && (
                          <button className="ss-btn ss-btn--ghost" onClick={() => setPendingTileArtClear(selected.id)} style={{ padding: '5px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, color: 'var(--ss-danger)' }}>Remove baked art</button>
                        )}
                      </div>
                      <input ref={tileArtFileInputRef} type="file" accept=".json,application/json" style={{ display: 'none' }}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) void importTileArtFile(selected.id, f); e.target.value = ''; }} />
                      {tileArtImportError && <div style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-danger)', lineHeight: 1.4 }}>{tileArtImportError}</div>}
                    </div>
                    {!builtIn && (
                      <button className="ss-btn ss-btn--ghost" onClick={() => setPendingTileDelete(selected.id)} style={{ alignSelf: 'flex-start', padding: '4px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, color: 'var(--ss-danger)' }}>Delete tile</button>
                    )}
                  </div>
                </Fragment>
              );
            })()}
            <M2Label>Map size</M2Label>
            <div style={{ padding: '0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
                W
                <input className="ss-input" type="number" min={4} max={200} value={mapWidth}
                  onChange={(e) => { const v = Math.round(Number(e.target.value)); if (Number.isFinite(v) && v >= 4 && v <= 200) resizeMap(v, mapHeight); }}
                  style={{ width: 56, padding: '5px 7px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, textAlign: 'center' }} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
                H
                <input className="ss-input" type="number" min={4} max={200} value={mapHeight}
                  onChange={(e) => { const v = Math.round(Number(e.target.value)); if (Number.isFinite(v) && v >= 4 && v <= 200) resizeMap(mapWidth, v); }}
                  style={{ width: 56, padding: '5px 7px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, textAlign: 'center' }} />
              </label>
            </div>
          </Fragment>
        )}
        {view === 'entities' && (
          <Fragment>
            <M2Label>Placing</M2Label>
            <TabStrip
              listLabel="Place mode tool"
              variant="segmented"
              tabs={PIXEL_PLACE_TOOL_TABS}
              activeId={placeTool}
              onSelect={(id) => setPlaceTool(id as typeof placeTool)}
              style={{ margin: '0 16px' }}
            />
            {placeTool === 'sprite' && (
              <Fragment>
            <M2Label>Sprite to place</M2Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
              {realSpriteNames.map((s) => (
                <button key={s} onClick={() => setPlaceSprite(s)} style={{
                  textAlign: 'left', padding: '7px 12px', border: 'none', borderRadius: 8,
                  background: effectivePlaceSprite === s ? 'var(--ss-accent-soft)' : 'transparent',
                  fontSize: 'var(--ss-fs-sm)', fontWeight: 450, color: effectivePlaceSprite === s ? 'var(--ss-accent)' : 'var(--ss-text-2)',
                }}>{s}{s === player ? ' · player' : ''}</button>
              ))}
            </div>
            <M2Label>Behaviour</M2Label>
            <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
                Kind
                <select className="ss-input" value={placeKind} onChange={(e) => setPlaceKind(e.target.value as Tilemap2DEntityKind)}
                  style={{ appearance: 'none', padding: '7px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 8 }}>
                  {ENTITY_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </label>
              <GsToggle label="Blocking (like a solid tile)" on={placeBlocking} onChange={setPlaceBlocking} />
            </div>
            <div style={{ padding: '8px 16px 0', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
              Click an empty cell to place the selected sprite with these settings; click a placed one to remove it. To change a placed entity&rsquo;s kind or blocking, remove and re-place it.
            </div>
            <M2Label>Patrol</M2Label>
            {npcEntities.length === 0 ? (
              <div style={{ padding: '0 16px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
                Place an entity with Kind &ldquo;NPC&rdquo; above to give it a waypoint route.
              </div>
            ) : (
              <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <select className="ss-input" value={patrolEntityId ?? ''} onChange={(e) => setPatrolEntityId(e.target.value || null)}
                  style={{ appearance: 'none', padding: '7px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 8 }}>
                  <option value="">Select a placed npc…</option>
                  {npcEntities.map((e) => (
                    <option key={e.id} value={e.id}>{e.spriteId} @ ({e.x}, {e.y}){e.patrol ? ' · patrolling' : ''}</option>
                  ))}
                </select>
                {patrolEntity && (
                  <Fragment>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', flex: 1 }}>
                        Loop
                        <select className="ss-input" value={patrolEntity.patrol?.loopMode ?? 'loop'}
                          onChange={(e) => patchPatrol(patrolEntity.id, (p) => ({ ...p, loopMode: e.target.value as PatrolLoopMode }))}
                          style={{ appearance: 'none', padding: '6px 8px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>
                          <option value="loop">Loop</option>
                          <option value="ping-pong">Ping-pong</option>
                          <option value="once">Once</option>
                        </select>
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', width: 70 }}>
                        Speed
                        <input className="ss-input" type="number" min={0.1} step={0.5} value={patrolEntity.patrol?.speed ?? 3}
                          onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v) && v > 0) patchPatrol(patrolEntity.id, (p) => ({ ...p, speed: v })); }}
                          style={{ padding: '6px 8px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }} />
                      </label>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {(patrolEntity.patrol?.points ?? []).map((pt, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', width: 12 }}>{i + 1}</span>
                          <input className="ss-input" type="number" title="X (tiles)" value={pt.x}
                            onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) updateWaypoint(patrolEntity.id, i, { x: v }); }}
                            style={{ width: 44, padding: '5px 6px', fontSize: 'var(--ss-fs-xs)', borderRadius: 6 }} />
                          <input className="ss-input" type="number" title="Y (tiles)" value={pt.y}
                            onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) updateWaypoint(patrolEntity.id, i, { y: v }); }}
                            style={{ width: 44, padding: '5px 6px', fontSize: 'var(--ss-fs-xs)', borderRadius: 6 }} />
                          <input className="ss-input" type="number" title="Dwell (ms)" min={0} step={100} value={pt.dwellMs ?? 0}
                            onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v) && v >= 0) updateWaypoint(patrolEntity.id, i, { dwellMs: v }); }}
                            style={{ width: 56, padding: '5px 6px', fontSize: 'var(--ss-fs-xs)', borderRadius: 6 }} />
                          <button onClick={() => removeWaypoint(patrolEntity.id, i)} title="Remove waypoint" style={{ width: 22, height: 22, borderRadius: 6, border: 'none', background: 'transparent', color: 'var(--ss-text-3)', fontSize: 'var(--ss-fs-sm)', flexShrink: 0 }}>×</button>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => addWaypoint(patrolEntity.id, patrolEntity.patrol?.points.at(-1)?.x ?? patrolEntity.x, patrolEntity.patrol?.points.at(-1)?.y ?? patrolEntity.y)}
                      style={{ padding: '6px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, border: '1px dashed var(--ss-border)', background: 'transparent', color: 'var(--ss-text-2)' }}
                    >
                      + Add waypoint
                    </button>
                    {patrolEntity.patrol && (
                      <button onClick={() => removePatrol(patrolEntity.id)} style={{ padding: '6px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, border: 'none', background: 'transparent', color: 'var(--ss-danger)' }}>
                        Clear patrol
                      </button>
                    )}
                    <div style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
                      X/Y are tile coordinates; the entity walks between waypoints in a straight line, dwelling at each for its own delay.
                    </div>
                  </Fragment>
                )}
              </div>
            )}
              </Fragment>
            )}
            {placeTool === 'spawn' && (
              <Fragment>
                <M2Label>Player spawn</M2Label>
                <div style={{ padding: '0 16px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-2)', lineHeight: 1.6 }}>
                  {spawn ? (
                    <Fragment>
                      Spawn set at ({spawn.x}, {spawn.y}).{' '}
                      <button onClick={() => setSpawn(null)} style={{ border: 'none', background: 'transparent', color: 'var(--ss-danger)', fontSize: 'var(--ss-fs-xs)', padding: 0 }}>Clear</button>
                    </Fragment>
                  ) : (
                    'No spawn set - the runtime falls back to the first walkable cell.'
                  )}
                </div>
                <div style={{ padding: '8px 16px 0', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
                  Click a cell to set the player&rsquo;s spawn point there.
                </div>
              </Fragment>
            )}
            {placeTool === 'trigger' && (
              <Fragment>
                <M2Label>Trigger</M2Label>
                <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
                    Kind
                    <select className="ss-input" value={triggerKind} onChange={(e) => setTriggerKind(e.target.value as Tilemap2DTriggerVolume['kind'])}
                      style={{ appearance: 'none', padding: '7px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 8 }}>
                      <option value="event">Event</option>
                      <option value="custom">Custom</option>
                      <option value="damage">Damage</option>
                      <option value="teleport">Teleport</option>
                    </select>
                  </label>
                  {triggerKind !== 'teleport' && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
                      Event name
                      <input className="ss-input" value={triggerEventName} onChange={(e) => setTriggerEventName(e.target.value)}
                        placeholder="e.g. door-open" style={{ padding: '7px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 8 }} />
                    </label>
                  )}
                  {triggerKind === 'teleport' && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', flex: 1 }}>
                        Target X
                        <input className="ss-input" type="number" value={triggerTargetX}
                          onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) setTriggerTargetX(v); }}
                          style={{ padding: '6px 8px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }} />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', flex: 1 }}>
                        Target Y
                        <input className="ss-input" type="number" value={triggerTargetY}
                          onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) setTriggerTargetY(v); }}
                          style={{ padding: '6px 8px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }} />
                      </label>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', flex: 1 }}>
                      Width (tiles)
                      <input className="ss-input" type="number" min={1} step={1} value={triggerW}
                        onChange={(e) => { const v = Math.round(Number(e.target.value)); if (Number.isFinite(v) && v > 0) setTriggerW(v); }}
                        style={{ padding: '6px 8px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }} />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', flex: 1 }}>
                      Height (tiles)
                      <input className="ss-input" type="number" min={1} step={1} value={triggerH}
                        onChange={(e) => { const v = Math.round(Number(e.target.value)); if (Number.isFinite(v) && v > 0) setTriggerH(v); }}
                        style={{ padding: '6px 8px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }} />
                    </label>
                  </div>
                </div>
                <div style={{ padding: '8px 16px 0', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
                  Click an empty cell to place a trigger of this size, anchored at that cell&rsquo;s top-left; click any cell inside a placed trigger to remove it. To change a placed trigger&rsquo;s kind or fields, remove and re-place it. Fires a Logic <code>trigger-enter</code>/<code>trigger-exit</code> event a rule can react to - author the matching rule in Logic mode.
                </div>
                {triggers.length > 0 && (
                  <div style={{ padding: '4px 16px 0', fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', lineHeight: 1.6 }}>
                    {triggers.length} placed: {triggers.map((t) => `${t.kind}@(${t.x},${t.y})`).join(', ')}
                  </div>
                )}
              </Fragment>
            )}
          </Fragment>
        )}
        <div style={{ marginTop: 'auto', padding: '14px 16px 0' }}>
          <button
            className={'ss-btn ss-btn--sm ' + (mapSyncState === 'error' ? 'ss-btn--primary' : 'ss-btn--ghost')}
            style={{ width: '100%', justifyContent: 'center' }}
            onClick={saveToAssetsNow}
            disabled={mapSyncState === 'saving'}
          >
            {mapSyncState === 'saving' ? 'Saving…' : mapSyncState === 'saved' ? 'Saved to project' : mapSyncState === 'error' ? 'Sync failed - retry' : 'Save now'}
          </button>
        </div>
      </M2Panel>

      {/* centre: canvas */}
      <div style={{ position: 'relative', flex: 1, minWidth: 0, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(ellipse 70% 60% at 50% 40%, var(--ss-surface), var(--ss-bg-2) 80%)', padding: 24 }}>
        {view === 'sprite' ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <SpriteCanvas size={size} grid={grid} onPaint={paintSprite} painting={painting} zoom={spriteZoom}
              under={onion && frameIndex > 0 ? clip.frames[frameIndex - 1] : null} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--ss-glass)', backdropFilter: 'blur(8px)', border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '5px 6px 5px 12px' }}>
              <span style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)' }}>Zoom</span>
              <button onClick={() => setSpriteZoom((z) => ZOOM_STEPS[Math.max(0, ZOOM_STEPS.indexOf(z) - 1)] ?? z)}
                disabled={spriteZoom === ZOOM_STEPS[0]}
                style={{ width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'var(--ss-bg-2)', color: 'var(--ss-text-2)', fontSize: 'var(--ss-fs-sm)', opacity: spriteZoom === ZOOM_STEPS[0] ? 0.4 : 1 }}>-</button>
              <span style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-2)', minWidth: 30, textAlign: 'center' }}>{spriteZoom}×</span>
              <button onClick={() => setSpriteZoom((z) => ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, ZOOM_STEPS.indexOf(z) + 1)] ?? z)}
                disabled={spriteZoom === ZOOM_STEPS[ZOOM_STEPS.length - 1]}
                style={{ width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'var(--ss-bg-2)', color: 'var(--ss-text-2)', fontSize: 'var(--ss-fs-sm)', opacity: spriteZoom === ZOOM_STEPS[ZOOM_STEPS.length - 1] ? 0.4 : 1 }}>+</button>
            </div>
            {/* flipbook frame strip, scoped to the active clip */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--ss-glass)', backdropFilter: 'blur(8px)', border: '1px solid var(--ss-border-soft)', borderRadius: 12, padding: '6px 10px' }}>
              <span style={{ fontSize: 'var(--ss-fs-3xs)', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ss-text-3)' }}>{activeClipName}</span>
              {clip.frames.length > 1 && (
                <button onClick={() => setPlaying((p) => !p)} title={playing ? 'Pause' : 'Play this clip at its own FPS'} aria-label={playing ? 'Pause' : 'Play'} style={{
                  width: 26, height: 26, borderRadius: 7, border: '1px solid ' + (playing ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)'),
                  background: playing ? 'var(--ss-accent-soft)' : 'transparent',
                  color: playing ? 'var(--ss-accent)' : 'var(--ss-text-3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--ss-fs-xs)',
                }}><PxPlayPauseIcon playing={playing} /></button>
              )}
              {clip.frames.map((frameCells, i) => (
                <button key={i} onClick={() => { setPlaying(false); setFrame(i); }} title={'Frame ' + (i + 1)} aria-label={'Frame ' + (i + 1)} style={{
                  width: 26, height: 26, borderRadius: 7, border: '1px solid ' + (i === frameIndex ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)'),
                  background: i === frameIndex ? 'var(--ss-accent-soft)' : 'transparent',
                  padding: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}><FrameThumb cells={frameCells} size={size} /></button>
              ))}
              <button onClick={addFrame} title="Add a frame (copies the current one)" style={{ width: 26, height: 26, borderRadius: 7, border: '1px dashed var(--ss-border)', background: 'transparent', color: 'var(--ss-text-3)', fontSize: 'var(--ss-fs-sm)' }}>+</button>
              {clip.frames.length > 1 && (
                <button onClick={deleteFrame} title="Delete this frame" style={{ width: 26, height: 26, borderRadius: 7, border: 'none', background: 'transparent', color: 'var(--ss-text-3)', fontSize: 'var(--ss-fs-sm)' }}>×</button>
              )}
              <span style={{ width: 1, height: 16, background: 'var(--ss-border-soft)' }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)' }}>
                FPS
                <input className="ss-input" value={clip.fps} onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v) && v > 0 && v <= 30) patchClip((c) => ({ ...c, fps: v })); }}
                  style={{ width: 40, padding: '3px 6px', fontSize: 'var(--ss-fs-xs)', borderRadius: 6, textAlign: 'center' }} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)' }} title="Loop this clip continuously; off plays once and holds the last frame">
                <button role="switch" aria-checked={clip.loop} className={'ss-switch' + (clip.loop ? ' is-on' : '')} onClick={() => patchClip((c) => ({ ...c, loop: !c.loop }))} aria-label="Loop" />
                LOOP
              </label>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--ss-fs-3xs)', fontWeight: 600, letterSpacing: '0.08em', color: 'var(--ss-text-3)' }} title="Onion skin: ghost the previous frame under this one">
                <button role="switch" aria-checked={onion} className={'ss-switch' + (onion ? ' is-on' : '')} onClick={() => setOnion(!onion)} aria-label="Onion skin" />
                ONION
              </span>
            </div>
          </div>
        ) : view === 'tilemap' ? (
          <div
            onMouseDown={(e) => { e.preventDefault(); painting.current = true; }}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(' + mapWidth + ', ' + mapCell + 'px)', border: '1px solid var(--ss-border)', borderRadius: 4, overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.5)', cursor: 'crosshair', userSelect: 'none' }}>
            {map.map((t, i) => (
              <div key={i}
                onMouseDown={() => paintMap(i)}
                onMouseEnter={() => { if (painting.current) paintMap(i); }}>
                <LayeredTileCell background={backgroundMap[i] ?? null} main={t} foreground={foregroundMap[i] ?? null} active={mapLayer} size={mapCell} tiles={tiles} />
              </div>
            ))}
          </div>
        ) : (
          <div
            onMouseDown={(e) => { e.preventDefault(); painting.current = true; }}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(' + mapWidth + ', ' + mapCell + 'px)', border: '1px solid var(--ss-border)', borderRadius: 4, overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.5)', cursor: 'crosshair', userSelect: 'none' }}>
            {map.map((t, i) => {
              const x = i % mapWidth;
              const y = (i / mapWidth) | 0;
              const entity = entities.find((e) => e.x === x && e.y === y);
              const entitySprite = entity ? sprites[entity.spriteId] : undefined;
              const entityFrames = entitySprite?.clips[entitySprite.activeClip]?.frames;
              const swatch = entityFrames?.[0]?.find((c) => c !== null) ?? null;
              const cellTrigger = triggerAt(x, y);
              const isSpawnCell = spawn?.x === x && spawn?.y === y;
              const entityTitle = entity
                ? entity.spriteId + (entity.blocking ? ' · blocking' : '') + (entity.kind !== 'decoration' ? ' · ' + entity.kind : '')
                : placeTool === 'spawn'
                ? (isSpawnCell ? 'Player spawn' : 'Click to set player spawn here')
                : placeTool === 'trigger'
                ? (cellTrigger ? cellTrigger.kind + ' trigger - click to remove' : 'Empty - click to place a ' + triggerKind + ' trigger')
                : 'Empty - click to place ' + effectivePlaceSprite;
              return (
                <div key={i}
                  onMouseDown={() => {
                    if (placeTool === 'spawn') setSpawn({ x, y });
                    else if (placeTool === 'trigger') placeOrRemoveTrigger(i);
                    else placeEntity(i);
                  }}
                  onMouseEnter={() => { if (painting.current && placeTool === 'sprite') placeEntity(i, true); }}
                  title={entityTitle}
                  style={{ position: 'relative' }}>
                  <TileCell tile={t} size={mapCell} tiles={tiles} />
                  {entity && (
                    <span style={{
                      position: 'absolute', inset: 3, borderRadius: entity.blocking ? 5 : '50%',
                      background: swatch ?? 'var(--ss-accent)',
                      border: '1.5px solid ' + (entity.blocking ? 'var(--ss-danger)' : 'rgba(255,255,255,0.7)'),
                      boxShadow: '0 1px 4px rgba(0,0,0,0.5)',
                    }} />
                  )}
                  {cellTrigger && (
                    <span style={{
                      position: 'absolute', inset: 1, border: '1.5px dashed #f5c451',
                      background: 'rgba(245,196,81,0.18)', pointerEvents: 'none',
                    }} />
                  )}
                  {isSpawnCell && (
                    <span style={{
                      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#7cfc8a', fontSize: 'var(--ss-fs-md)', textShadow: '0 1px 2px rgba(0,0,0,0.8)', pointerEvents: 'none',
                    }}>⚑</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <span style={{
          position: 'absolute', top: 14, left: 14, display: 'inline-flex', alignItems: 'center', gap: 8,
          background: 'var(--ss-glass)', backdropFilter: 'blur(8px)', border: '1px solid var(--ss-border-soft)',
          borderRadius: 999, padding: '7px 14px', fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 'var(--ss-fs-2xs)', letterSpacing: '0.05em', color: 'var(--ss-text-2)', pointerEvents: 'none',
        }}>{view === 'sprite' ? displayActive + '.' + activeClipName + ' · ' + size + '×' + size + (clip.frames.length > 1 ? ' · frame ' + (frameIndex + 1) + '/' + clip.frames.length : '') : 'overworld.tilemap · ' + mapWidth + '×' + mapHeight + (view === 'entities' ? ' · ' + entities.length + ' placed' : view === 'tilemap' ? ' · ' + mapLayer + ' layer' : '')}</span>
      </div>

      {/* right: tools + palette */}
      <M2Panel side="right" width={260}>
        <M2Label>Tools</M2Label>
        <div style={{ display: 'flex', gap: 6, padding: '0 16px' }}>
          {view !== 'entities' && (['pencil', 'eraser', 'fill', 'pick'] as PxToolKind[]).map((t) => (
            <button key={t} onClick={() => setTool(t)} title={t.charAt(0).toUpperCase() + t.slice(1) + ' · ' + PX_TOOL_HOTKEYS[t]} style={{
              width: 38, height: 36, borderRadius: 9,
              border: '1px solid ' + (tool === t ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)'),
              background: tool === t ? 'var(--ss-accent-soft)' : 'transparent',
              color: tool === t ? 'var(--ss-accent)' : 'var(--ss-text-3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><PxToolIcon kind={t} /></button>
          ))}
          {/* Undo/redo cover entity placement too (Place mode drag-to-place-many
              can remove a whole row of entities in one stroke), so these stay
              visible in every view, not just the paint tools. */}
          <button onClick={undo} disabled={undoStack.length === 0} title="Undo · Ctrl+Z" style={{
            width: 38, height: 36, borderRadius: 9, marginLeft: 'auto',
            border: '1px solid var(--ss-border-soft)', background: 'transparent',
            color: undoStack.length === 0 ? 'var(--ss-text-3)' : 'var(--ss-text-2)', opacity: undoStack.length === 0 ? 0.4 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><PxUndoIcon flip={false} /></button>
          <button onClick={redo} disabled={redoStack.length === 0} title="Redo · Ctrl+Shift+Z" style={{
            width: 38, height: 36, borderRadius: 9,
            border: '1px solid var(--ss-border-soft)', background: 'transparent',
            color: redoStack.length === 0 ? 'var(--ss-text-3)' : 'var(--ss-text-2)', opacity: redoStack.length === 0 ? 0.4 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><PxUndoIcon flip /></button>
        </div>
        {view === 'sprite' && (
          <PalettePanel palettes={palettes} setPalettes={setPalettes} activePal={activePal} setActivePal={setActivePal} colour={colour} setColour={setColour} onDeletePalette={setPendingPaletteDelete} />
        )}
        {view === 'tilemap' && (
          <Fragment>
            <M2Label>Map</M2Label>
            <div style={{ padding: '0 16px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.6 }}>
              Paint with the selected tile onto the {mapLayer} layer. Eraser clears cells.{' '}
              {mapLayer === 'main'
                ? 'Collision comes from this layer automatically - mark a tile Solid on the left to block the player.'
                : <>Background and Foreground are visual only - collision always comes from the Main layer, even where it&rsquo;s empty here.</>}
            </div>
          </Fragment>
        )}
        {view === 'entities' && (
          <Fragment>
            <M2Label>Place</M2Label>
            <div style={{ padding: '0 16px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.6 }}>
              Click a cell to place the selected sprite there, or click a placed one to remove it. Drag to place (or remove) across many cells at once.
            </div>
          </Fragment>
        )}
        <div style={{ padding: '20px 16px 0', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
          Sprites and tiles save automatically, and sync into your game a moment after you stop editing; 2D projects play them in Preview and on the published page. Use Save now to sync immediately.
        </div>
      </M2Panel>
      {pendingResize !== null && (
        <EdConfirmDialog
          title={'Shrink canvas to ' + pendingResize + '×' + pendingResize + '?'}
          body={'Pixels outside the new canvas will be cropped away on every frame. Growing the canvas never loses art.'}
          confirmLabel="Shrink and crop"
          onConfirm={() => { applyResize(pendingResize); setPendingResize(null); }}
          onCancel={() => setPendingResize(null)}
        />
      )}
      {pendingMapResize !== null && (
        <EdConfirmDialog
          title={'Shrink map to ' + pendingMapResize.w + '×' + pendingMapResize.h + '?'}
          body={'Painted tiles and placed sprites outside the new size will be cropped away. Growing the map never loses anything.'}
          confirmLabel="Shrink and crop"
          onConfirm={() => { applyMapResize(pendingMapResize.w, pendingMapResize.h); setPendingMapResize(null); }}
          onCancel={() => setPendingMapResize(null)}
        />
      )}
      {pendingDelete !== null && (
        <EdConfirmDialog
          title={'Delete ' + pendingDelete + '?'}
          body={'This removes the sprite and all its frames from the project.'}
          confirmLabel="Delete sprite"
          onConfirm={() => { deleteSprite(pendingDelete); setPendingDelete(null); }}
          onCancel={() => setPendingDelete(null)}
        />
      )}
      {pendingTileDelete !== null && (
        <EdConfirmDialog
          title={'Delete tile "' + pendingTileDelete + '"?'}
          body={'This clears every cell painted with this tile across the whole map, not just this one.'}
          confirmLabel="Delete tile"
          onConfirm={() => { deleteTile(pendingTileDelete); setPendingTileDelete(null); }}
          onCancel={() => setPendingTileDelete(null)}
        />
      )}
      {pendingTileArtClear !== null && (
        <EdConfirmDialog
          title={'Remove baked art from "' + pendingTileArtClear + '"?'}
          body="This clears the baked pixel art on this tile; it falls back to its base and accent colours. The tile itself is not deleted."
          confirmLabel="Remove baked art"
          onConfirm={() => { clearTileArt(pendingTileArtClear); setPendingTileArtClear(null); }}
          onCancel={() => setPendingTileArtClear(null)}
        />
      )}
      {pendingPaletteDelete !== null && (
        <EdConfirmDialog
          title={'Delete palette "' + (palettes[pendingPaletteDelete]?.name ?? '') + '"?'}
          body="This removes every colour in it. Colours already painted with it are unaffected."
          confirmLabel="Delete palette"
          onConfirm={() => { deletePalette(pendingPaletteDelete); setPendingPaletteDelete(null); }}
          onCancel={() => setPendingPaletteDelete(null)}
        />
      )}
      {pendingImport !== null && (
        <EdConfirmDialog
          title={'Replace ' + pendingImport.id + '?'}
          body={'A sprite named "' + pendingImport.id + '" already exists in this project. Importing will overwrite it with the contents of this file.'}
          confirmLabel="Replace sprite"
          onConfirm={() => {
            setSprites((all) => ({ ...all, [pendingImport.id]: pendingImport.sprite }));
            setActive(pendingImport.id);
            setFrame(0);
            setPendingImport(null);
          }}
          onCancel={() => setPendingImport(null)}
        />
      )}
      {spritesheetImportOpen && (
        <PxSpritesheetImportDialog
          existingNames={realSpriteNames}
          onImport={importSlicedSprites}
          onCancel={() => setSpritesheetImportOpen(false)}
        />
      )}
    </div>
  );
}
